import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  setDoc,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { db } from "../firebase/client";
import type { User } from "firebase/auth";
import React, { useState, useEffect, useRef } from "react";
import { AppTab } from "../contexts/types";
import { useLanguage } from "../contexts/LanguageContext";
import { usePatient } from "../contexts/PatientContext";
import { detectFaceData } from "../services/geminiService";
import { getAdminAccessClaim, isAdminAccessAllowed } from "../utils/adminAccess";
import { verifyProfessionalCredentials } from "../services/professionalVerificationService";
import type { ProfessionalVerificationInfo } from "../types/professionalVerification";
import { buildBannerConfig, resolveOrthoBannerTemplateAsset } from "../utils/doctorProfile";
import { generateOrthoBannerOptions } from "../services/geminiService";
import PdfHeaderPreview from "./PdfHeaderPreview";
import { useUsageLimits } from "../hooks/useUsageLimits";
import { TrialBadge, TrialBanner } from "./TrialBanner";
import {
  Activity,
  BarChart3,
  BookOpen,
  Calendar,
  FileText,
  ScanText,
  Package,
  UserRound,
  ChevronRight,
  SearchCode,
  Settings,
  Wallet,
  Save,
  X,
  QrCode,
  CreditCard,
  Building2,
  Trash2,
  Image as ImageIcon,
  Users,
  LogOut,
  Upload,
  ShieldCheck,
  Stethoscope,
  MapPin,
  Phone,
  Mail,
  Award,
  IdCard,
  Check,
  Pen,
  Camera,
  Move,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Sparkles,
  Palette,
  Cloud,
  LifeBuoy,
} from "lucide-react";

type LayoutProps = {
  activeTab: AppTab;
  setActiveTab: (tab: AppTab) => void;
  currentUser: User;
  onLogout: () => Promise<void>;

  // ✅ novos props (assinatura)
  plan?: {
  tier?: string;
  status?: string;
};
  onOpenSubscribeClick?: (tier?: "pro" | "premium") => void;
  professionalVerification: ProfessionalVerificationInfo;
  isProfessionalAccessBypassed: boolean;
  professionalAccessMessage?: string | null;
  onDismissProfessionalAccessMessage?: () => void;
  onBlockedClinicalAccess?: () => void;
  children: React.ReactNode;
};

interface AdminPaymentConfig {
  // Pix Data
  pixKey: string;
  pixBeneficiary: string; // Nome
  pixBank: string;
  pixQrCode: string; // Base64 string for the image
  pixCpf: string;

  // CC Data (Conta de Recebimento)
  ccBeneficiary: string; // Nome Titular da conta que recebe
  ccBank: string;
  ccAgency: string;
  ccAccount: string;
  ccCpf: string;
}

type PendingVerificationUser = {
  uid: string;
  name: string;
  email: string;
  crm: string;
  crmState: string;
  rqe: string;
  specialty?: string;
  method?: string;
  status?: string;
  submittedAt: any;
};

const patientSummaryAnatomyTranslationKeyMap: Record<string, string> = {
  "Clavícula": "patientAnatomyClavicle",
  "Ombro": "patientAnatomyShoulderProximalHumerus",
  "Braço": "patientAnatomyHumeralShaft",
  "Cotovelo": "patientAnatomyElbow",
  "Antebraço": "patientAnatomyForearm",
  "Punho": "patientAnatomyDistalRadius",
  "Mão": "patientAnatomyHand",
  "Coluna cervical": "patientAnatomyCervicalSpine",
  "Coluna torácica": "patientAnatomyThoracicSpine",
  "Coluna lombar": "patientAnatomyLumbarSpine",
  "Pelve": "patientAnatomyPelvis",
  "Quadril": "patientAnatomyHipAcetabulum",
  "Coxa": "patientAnatomyFemoralShaft",
  "Joelho": "patientAnatomyKnee",
  "Perna": "patientAnatomyTibialShaft",
  "Tornozelo": "patientAnatomyAnkle",
  "Pé": "patientAnatomyFootCalcaneus",
};

const patientSummaryLegacyAnatomyEntries = [
  ["clavicula", "Clavícula"],
  ["ombro (proximal humerus)", "Ombro"],
  ["ombro (úmero proximal)", "Ombro"],
  ["ombro", "Ombro"],
  ["diáfise umeral", "Braço"],
  ["diafise umeral", "Braço"],
  ["cotovelo", "Cotovelo"],
  ["rádio distal", "Punho"],
  ["radio distal", "Punho"],
  ["punho (escafoide)", "Punho"],
  ["mão", "Mão"],
  ["mao", "Mão"],
  ["coluna cervical", "Coluna cervical"],
  ["coluna torácica", "Coluna torácica"],
  ["coluna toracica", "Coluna torácica"],
  ["coluna lombar", "Coluna lombar"],
  ["pelve", "Pelve"],
  ["quadril (acetábulo)", "Quadril"],
  ["quadril (acetabulo)", "Quadril"],
  ["quadril", "Quadril"],
  ["fêmur proximal", "Quadril"],
  ["femur proximal", "Quadril"],
  ["fêmur diáfise", "Coxa"],
  ["femur diafise", "Coxa"],
  ["joelho", "Joelho"],
  ["platô tibial", "Joelho"],
  ["plato tibial", "Joelho"],
  ["tíbia diáfise", "Perna"],
  ["tibia diafise", "Perna"],
  ["perna", "Perna"],
  ["tornozelo", "Tornozelo"],
  ["pé (calcâneo)", "Pé"],
  ["pé (calcaneo)", "Pé"],
  ["pe (calcaneo)", "Pé"],
  ["calcâneo", "Pé"],
  ["calcaneo", "Pé"],
  ["pe", "Pé"],
  ["braco", "Braço"],
  ["antebraco", "Antebraço"],
  ["coxa", "Coxa"],
  ["coluna toracica", "Coluna torácica"],
] as const;

const patientSummaryLegacyAnatomyLabelMap = Object.fromEntries(patientSummaryLegacyAnatomyEntries) as Record<string, string>;

const normalizePatientSummaryAnatomyValue = (value?: string) => {
  const lookupKey = String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

  if (!lookupKey) return "";

  return patientSummaryLegacyAnatomyLabelMap[lookupKey] || String(value || "").trim();
};

const Layout: React.FC<LayoutProps> = ({
  children,
  activeTab,
  setActiveTab,
  currentUser,
  onLogout,
  plan,
  onOpenSubscribeClick,
  professionalVerification,
  isProfessionalAccessBypassed,
}) => {
  const { language, setLanguage, t } = useLanguage();
  const { activePatient } = usePatient();
  const translatePatientSummaryAnatomy = (value?: string) => {
    if (!value) return "";
    const normalizedValue = normalizePatientSummaryAnatomyValue(value);
    return t(patientSummaryAnatomyTranslationKeyMap[normalizedValue] || normalizedValue);
  };
  const getPatientSummarySideLabel = (side?: "Left" | "Right" | "Bilateral") =>
    side === "Left" ? t("left") : side === "Right" ? t("right") : t("bilateral");

  const [showAdminConfig, setShowAdminConfig] = useState(false);
  const [showOrthoID, setShowOrthoID] = useState(false);
  const [showProfessionalVerification, setShowProfessionalVerification] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
const [showFeedbackModal, setShowFeedbackModal] = useState(false);
const [feedbackType, setFeedbackType] = useState<"suggestion" | "complaint">("suggestion");
const [feedbackText, setFeedbackText] = useState("");
const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
const [feedbackNotice, setFeedbackNotice] = useState<string | null>(null);
  const [lockedPlanModal, setLockedPlanModal] = useState<{
  open: boolean;
  tab: AppTab | null;
}>({
  open: false,
  tab: null,
});

const openLockedPlanModal = (tab: AppTab) => {
  setLockedPlanModal({
    open: true,
    tab,
  });
};

const closeLockedPlanModal = () => {
  setLockedPlanModal({
    open: false,
    tab: null,
  });
};
  const [configTab, setConfigTab] = useState<"pix" | "cc" | "verification">("pix");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const profileInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);

  const photoContainerRef = useRef<HTMLDivElement>(null);

  // Current User Data
  const [userData, setUserData] = useState<any>(null);
  const [editFormData, setEditFormData] = useState<any>({});

  // Photo Dragging State (User Profile)
  const [isDraggingPhoto, setIsDraggingPhoto] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialPosPercent, setInitialPosPercent] = useState({ x: 0, y: 0 });
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingBanner, setIsUploadingBanner] = useState(false);
  const [logoUploadError, setLogoUploadError] = useState("");
  const [bannerUploadError, setBannerUploadError] = useState("");
  const [bannerAiError, setBannerAiError] = useState("");
  const [bannerAiOptions, setBannerAiOptions] = useState<any[]>([]);
  const [isGeneratingBanner, setIsGeneratingBanner] = useState(false);
  const bannerModeStorageKey = `orthoprecision_doctor_banner_mode_${currentUser?.uid || currentUser?.email || "default"}`;

  const [isAutoAligning, setIsAutoAligning] = useState(false);
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [backupStatus, setBackupStatus] = useState<
    "idle" | "preparing" | "authenticating" | "uploading" | "success" | "error"
  >("idle");

  const [config, setConfig] = useState<AdminPaymentConfig>({
    pixKey: "",
    pixBeneficiary: "",
    pixBank: "",
    pixQrCode: "",
    pixCpf: "",
    ccBeneficiary: "",
    ccBank: "",
    ccAgency: "",
    ccAccount: "",
    ccCpf: "",
  });
  const [verificationForm, setVerificationForm] = useState({
    crm: "",
    crmState: "",
    rqe: "",
    specialty: "",
  });
  const [verificationError, setVerificationError] = useState("");
  const [isSubmittingVerification, setIsSubmittingVerification] = useState(false);
  const [verificationNotice, setVerificationNotice] = useState<string | null>(null);
  const [pendingVerificationUsers, setPendingVerificationUsers] = useState<PendingVerificationUser[]>([]);
  const [verificationActionUid, setVerificationActionUid] = useState<string | null>(null);
  const [hasAdminClaim, setHasAdminClaim] = useState(false);

  // ✅ ADMIN: ajuste aqui conforme sua regra real
  // Opção 1: por e-mail
  const isAdmin = isAdminAccessAllowed(currentUser?.email, userData, {
    admin: hasAdminClaim,
  });
  // Opção 2 (se quiser): por UID
  // const isAdmin = currentUser?.uid === "SEU_UID_ADMIN_AQUI";

  // ... continua seu Layout abaixo
  
  useEffect(() => {
    let cancelled = false;

    const loadAdminClaim = async () => {
      if (!currentUser) {
        setHasAdminClaim(false);
        return;
      }

      const nextHasAdminClaim = await getAdminAccessClaim(currentUser);
      if (!cancelled) {
        setHasAdminClaim(nextHasAdminClaim);
      }
    };

    void loadAdminClaim();

    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  // Load User Data
  useEffect(() => {
  if (currentUser && !isAdmin) {
    const usersStr = localStorage.getItem("orthoprecision_users");
    let found = null;

    if (usersStr) {
      const users = JSON.parse(usersStr);
      found = users.find(
        (u: any) =>
          u.email === currentUser.email || u.username === currentUser.email
      );
    }

    if (found) {
      const data = {
        ...found,
        photoConfig: found.photoConfig || { x: 0, y: 0, scale: 1 },
        headerName: found.headerName || found.brandingHeaderName || found.name || "OrthoPrecision",
        headerTitlePrimary: found.headerTitlePrimary || found.brandingHeaderTitlePrimary || "ORTHO PRECISION",
        headerTitleSecondary: found.headerTitleSecondary || found.brandingHeaderTitleSecondary || "MEDICAL CREDENTIAL",
        bannerMode: found.bannerMode || found.brandingBannerMode || "ai",
        bannerSupportText: found.bannerSupportText || found.brandingBannerSupportText || "",
        bannerTextColor: found.bannerTextColor || found.brandingBannerTextColor || "",
        bannerPrimaryColor: found.bannerPrimaryColor || found.brandingBannerPrimaryColor || "",
        bannerAccentColor: found.bannerAccentColor || found.brandingBannerAccentColor || "",
        bannerBackgroundStyle: found.bannerBackgroundStyle || found.brandingBannerBackgroundStyle || "",
        bannerLayoutVariant: found.bannerLayoutVariant || found.brandingBannerLayoutVariant || "",
        banner: found.banner || found.brandingBanner || found.bannerUrl || "",
      };
      setUserData(data);
      setEditFormData(data);
    } else {
      const fallbackData = {
        name: currentUser.displayName || "Médico Responsável",
        headerName: currentUser.displayName || "OrthoPrecision",
        headerTitlePrimary: "ORTHO PRECISION",
        headerTitleSecondary: "MEDICAL CREDENTIAL",
        bannerMode: "ai",
        bannerSupportText: "",
        bannerTextColor: "",
        bannerPrimaryColor: "",
        bannerAccentColor: "",
        bannerBackgroundStyle: "",
        bannerLayoutVariant: "",
        banner: "",
        crm: "",
        rqe: "",
        gender: "",
        crmState: "",
        professionalVerification,
        hospital: "",
        phone: "",
        email: currentUser.email || "",
        website: "",
        username: currentUser.email || "",
        photo: "",
        photoConfig: { x: 0, y: 0, scale: 1 },
      };

      setUserData(fallbackData);
      setEditFormData(fallbackData);
    }
  } else if (isAdmin) {
    const usersStr = localStorage.getItem("orthoprecision_users");
    let adminData = {
      name: "Administrador do Sistema",
      crm: "000000/SP",
      rqe: "00000",
      gender: "male",
      crmState: "SP",
      professionalVerification: {
        status: "verified",
        crm: "000000",
        crmState: "SP",
        rqe: "00000",
      },
      hospital: "Hospital Central",
      phone: "(11) 99999-9999",
      email: "admin@orthoprecision.com",
      username: "admin",
      bannerSupportText: "",
      bannerTextColor: "",
      bannerPrimaryColor: "",
      bannerAccentColor: "",
      bannerBackgroundStyle: "",
      bannerLayoutVariant: "",
      photoConfig: { x: 0, y: 0, scale: 1 },
    };

    if (usersStr) {
      const users = JSON.parse(usersStr);
      const foundAdmin = users.find((u: any) => u.username === "admin");
      if (foundAdmin) {
      adminData = {
      ...adminData,
      ...foundAdmin,
      photoConfig: foundAdmin.photoConfig || { x: 0, y: 0, scale: 1 },
      headerName: foundAdmin.headerName || foundAdmin.brandingHeaderName || "OrthoPrecision",
          headerTitlePrimary: foundAdmin.headerTitlePrimary || foundAdmin.brandingHeaderTitlePrimary || "ORTHO PRECISION",
          headerTitleSecondary: foundAdmin.headerTitleSecondary || foundAdmin.brandingHeaderTitleSecondary || "MEDICAL CREDENTIAL",
          bannerMode: foundAdmin.bannerMode || foundAdmin.brandingBannerMode || "ai",
          bannerSupportText: foundAdmin.bannerSupportText || foundAdmin.brandingBannerSupportText || "",
          bannerTextColor: foundAdmin.bannerTextColor || foundAdmin.brandingBannerTextColor || "",
          bannerPrimaryColor: foundAdmin.bannerPrimaryColor || foundAdmin.brandingBannerPrimaryColor || "",
          bannerAccentColor: foundAdmin.bannerAccentColor || foundAdmin.brandingBannerAccentColor || "",
          bannerBackgroundStyle: foundAdmin.bannerBackgroundStyle || foundAdmin.brandingBannerBackgroundStyle || "",
          bannerLayoutVariant: foundAdmin.bannerLayoutVariant || foundAdmin.brandingBannerLayoutVariant || "",
          banner: foundAdmin.banner || foundAdmin.brandingBanner || foundAdmin.bannerUrl || "",
        };
      }
    }

    setUserData(adminData);
    setEditFormData(adminData);
  }
}, [currentUser, isAdmin]);

  useEffect(() => {
    let cancelled = false;

    const loadFirestoreProfile = async () => {
      if (!currentUser || isAdmin) return;

      try {
        const snap = await getDoc(doc(db, "users", currentUser.uid));
        if (!snap.exists()) return;

        const raw = snap.data() as any;
        const profile = raw?.profile || {};

        if (cancelled) return;

        setUserData((prev: any) => ({
          ...prev,
          ...raw,
          ...profile,
          name:
            profile.name ||
            raw.name ||
            prev?.name ||
            currentUser.displayName ||
            "Médico Responsável",
          crm: profile.crm || raw.crm || prev?.crm || "",
          rqe: profile.rqe || raw.rqe || prev?.rqe || "",
          crmState: profile.crmState || raw.crmState || prev?.crmState || "",
          gender:
            profile.gender ||
            raw.gender ||
            prev?.gender ||
            prev?.sexo ||
            "",
          professionalVerification:
            raw.professionalVerification ||
            prev?.professionalVerification ||
            professionalVerification,
          hospital: profile.hospital || raw.hospital || prev?.hospital || "",
          phone: profile.phone || raw.phone || prev?.phone || "",
          email:
            profile.email ||
            raw.email ||
            prev?.email ||
            currentUser.email ||
            "",
          website: profile.website || raw.website || prev?.website || "",
          username:
            raw.username ||
            prev?.username ||
            currentUser.email ||
            currentUser.uid,
          photo: prev?.photo || profile.photo || raw.photo || "",
          photoConfig:
            prev?.photoConfig ||
            profile.photoConfig ||
            raw.photoConfig ||
            { x: 0, y: 0, scale: 1 },
          banner:
            profile.banner ||
            profile.brandingBanner ||
            raw.banner ||
            raw.brandingBanner ||
            prev?.banner ||
            "",
          headerName:
            profile.headerName ||
            profile.brandingHeaderName ||
            raw.headerName ||
            prev?.headerName ||
            currentUser.displayName ||
            "OrthoPrecision",
          bannerMode:
            profile.bannerMode ||
            profile.brandingBannerMode ||
            raw.bannerMode ||
            prev?.bannerMode ||
            "ai",
          bannerTemplateId:
            profile.bannerTemplateId ||
            profile.brandingBannerTemplateId ||
            raw.bannerTemplateId ||
            prev?.bannerTemplateId ||
            "premium",
          bannerTagline:
            profile.bannerTagline ||
            profile.brandingBannerTagline ||
            raw.bannerTagline ||
            prev?.bannerTagline ||
            "",
          bannerSupportText:
            profile.bannerSupportText ||
            profile.brandingBannerSupportText ||
            raw.bannerSupportText ||
            prev?.bannerSupportText ||
            "",
          bannerAccentTone:
            profile.bannerAccentTone ||
            profile.brandingBannerAccentTone ||
            raw.bannerAccentTone ||
            prev?.bannerAccentTone ||
            "",
          bannerTextColor:
            profile.bannerTextColor ||
            profile.brandingBannerTextColor ||
            raw.bannerTextColor ||
            prev?.bannerTextColor ||
            "",
          bannerPrimaryColor:
            profile.bannerPrimaryColor ||
            profile.brandingBannerPrimaryColor ||
            raw.bannerPrimaryColor ||
            prev?.bannerPrimaryColor ||
            "",
          bannerAccentColor:
            profile.bannerAccentColor ||
            profile.brandingBannerAccentColor ||
            raw.bannerAccentColor ||
            prev?.bannerAccentColor ||
            "",
          bannerBackgroundStyle:
            profile.bannerBackgroundStyle ||
            profile.brandingBannerBackgroundStyle ||
            raw.bannerBackgroundStyle ||
            prev?.bannerBackgroundStyle ||
            "",
          bannerLayoutVariant:
            profile.bannerLayoutVariant ||
            profile.brandingBannerLayoutVariant ||
            raw.bannerLayoutVariant ||
            prev?.bannerLayoutVariant ||
            "",
          bannerLayoutPreset:
            profile.bannerLayoutPreset ||
            profile.brandingBannerLayoutPreset ||
            raw.bannerLayoutPreset ||
            prev?.bannerLayoutPreset ||
            "balanced",
          bannerOffsetLeftX:
            profile.bannerOffsetLeftX ??
            profile.brandingBannerOffsetLeftX ??
            raw.bannerOffsetLeftX ??
            prev?.bannerOffsetLeftX ??
            0,
          bannerOffsetLeftY:
            profile.bannerOffsetLeftY ??
            profile.brandingBannerOffsetLeftY ??
            raw.bannerOffsetLeftY ??
            prev?.bannerOffsetLeftY ??
            0,
          bannerOffsetRightX:
            profile.bannerOffsetRightX ??
            profile.brandingBannerOffsetRightX ??
            raw.bannerOffsetRightX ??
            prev?.bannerOffsetRightX ??
            0,
          bannerOffsetRightY:
            profile.bannerOffsetRightY ??
            profile.brandingBannerOffsetRightY ??
            raw.bannerOffsetRightY ??
            prev?.bannerOffsetRightY ??
            0,
          headerTitlePrimary:
            profile.headerTitlePrimary ||
            profile.brandingHeaderTitlePrimary ||
            raw.headerTitlePrimary ||
            prev?.headerTitlePrimary ||
            "ORTHO PRECISION",
          headerTitleSecondary:
            profile.headerTitleSecondary ||
            profile.brandingHeaderTitleSecondary ||
            raw.headerTitleSecondary ||
            prev?.headerTitleSecondary ||
            "MEDICAL CREDENTIAL",
        }));

        setEditFormData((prev: any) => ({
          ...prev,
          ...raw,
          ...profile,
          name:
            profile.name ||
            raw.name ||
            prev?.name ||
            currentUser.displayName ||
            "Médico Responsável",
          crm: profile.crm || raw.crm || prev?.crm || "",
          rqe: profile.rqe || raw.rqe || prev?.rqe || "",
          crmState: profile.crmState || raw.crmState || prev?.crmState || "",
          gender:
            profile.gender ||
            raw.gender ||
            prev?.gender ||
            prev?.sexo ||
            "",
          professionalVerification:
            raw.professionalVerification ||
            prev?.professionalVerification ||
            professionalVerification,
          hospital: profile.hospital || raw.hospital || prev?.hospital || "",
          phone: profile.phone || raw.phone || prev?.phone || "",
          email:
            profile.email ||
            raw.email ||
            prev?.email ||
            currentUser.email ||
            "",
          website: profile.website || raw.website || prev?.website || "",
          username:
            raw.username ||
            prev?.username ||
            currentUser.email ||
            currentUser.uid,
          photo: prev?.photo || profile.photo || raw.photo || "",
          photoConfig:
            prev?.photoConfig ||
            profile.photoConfig ||
            raw.photoConfig ||
            { x: 0, y: 0, scale: 1 },
          banner:
            profile.banner ||
            profile.brandingBanner ||
            raw.banner ||
            raw.brandingBanner ||
            prev?.banner ||
            "",
          headerName:
            profile.headerName ||
            profile.brandingHeaderName ||
            raw.headerName ||
            prev?.headerName ||
            currentUser.displayName ||
            "OrthoPrecision",
          bannerMode:
            profile.bannerMode ||
            profile.brandingBannerMode ||
            raw.bannerMode ||
            prev?.bannerMode ||
            "ai",
          bannerTemplateId:
            profile.bannerTemplateId ||
            profile.brandingBannerTemplateId ||
            raw.bannerTemplateId ||
            prev?.bannerTemplateId ||
            "premium",
          bannerTagline:
            profile.bannerTagline ||
            profile.brandingBannerTagline ||
            raw.bannerTagline ||
            prev?.bannerTagline ||
            "",
          bannerSupportText:
            profile.bannerSupportText ||
            profile.brandingBannerSupportText ||
            raw.bannerSupportText ||
            prev?.bannerSupportText ||
            "",
          bannerAccentTone:
            profile.bannerAccentTone ||
            profile.brandingBannerAccentTone ||
            raw.bannerAccentTone ||
            prev?.bannerAccentTone ||
            "",
          bannerTextColor:
            profile.bannerTextColor ||
            profile.brandingBannerTextColor ||
            raw.bannerTextColor ||
            prev?.bannerTextColor ||
            "",
          bannerPrimaryColor:
            profile.bannerPrimaryColor ||
            profile.brandingBannerPrimaryColor ||
            raw.bannerPrimaryColor ||
            prev?.bannerPrimaryColor ||
            "",
          bannerAccentColor:
            profile.bannerAccentColor ||
            profile.brandingBannerAccentColor ||
            raw.bannerAccentColor ||
            prev?.bannerAccentColor ||
            "",
          bannerBackgroundStyle:
            profile.bannerBackgroundStyle ||
            profile.brandingBannerBackgroundStyle ||
            raw.bannerBackgroundStyle ||
            prev?.bannerBackgroundStyle ||
            "",
          bannerLayoutVariant:
            profile.bannerLayoutVariant ||
            profile.brandingBannerLayoutVariant ||
            raw.bannerLayoutVariant ||
            prev?.bannerLayoutVariant ||
            "",
          bannerLayoutPreset:
            profile.bannerLayoutPreset ||
            profile.brandingBannerLayoutPreset ||
            raw.bannerLayoutPreset ||
            prev?.bannerLayoutPreset ||
            "balanced",
          bannerOffsetLeftX:
            profile.bannerOffsetLeftX ??
            profile.brandingBannerOffsetLeftX ??
            raw.bannerOffsetLeftX ??
            prev?.bannerOffsetLeftX ??
            0,
          bannerOffsetLeftY:
            profile.bannerOffsetLeftY ??
            profile.brandingBannerOffsetLeftY ??
            raw.bannerOffsetLeftY ??
            prev?.bannerOffsetLeftY ??
            0,
          bannerOffsetRightX:
            profile.bannerOffsetRightX ??
            profile.brandingBannerOffsetRightX ??
            raw.bannerOffsetRightX ??
            prev?.bannerOffsetRightX ??
            0,
          bannerOffsetRightY:
            profile.bannerOffsetRightY ??
            profile.brandingBannerOffsetRightY ??
            raw.bannerOffsetRightY ??
            prev?.bannerOffsetRightY ??
            0,
          headerTitlePrimary:
            profile.headerTitlePrimary ||
            profile.brandingHeaderTitlePrimary ||
            raw.headerTitlePrimary ||
            prev?.headerTitlePrimary ||
            "ORTHO PRECISION",
          headerTitleSecondary:
            profile.headerTitleSecondary ||
            profile.brandingHeaderTitleSecondary ||
            raw.headerTitleSecondary ||
            prev?.headerTitleSecondary ||
            "MEDICAL CREDENTIAL",
        }));
      } catch (error) {
        console.error("Erro ao carregar perfil do Firestore:", error);
      }
    };

    void loadFirestoreProfile();

    return () => {
      cancelled = true;
    };
  }, [currentUser, isAdmin]);

  useEffect(() => {
    const saved = localStorage.getItem('orthoprecision_admin_payment');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setConfig({
          pixKey: parsed.pixKey || '',
          pixBeneficiary: parsed.pixBeneficiary || '',
          pixBank: parsed.pixBank || '',
          pixQrCode: parsed.pixQrCode || '',
          pixCpf: parsed.pixCpf || '',
          ccBeneficiary: parsed.ccBeneficiary || '',
          ccBank: parsed.ccBank || '',
          ccAgency: parsed.ccAgency || '',
          ccAccount: parsed.ccAccount || '',
          ccCpf: parsed.ccCpf || ''
        });
      } catch (e) {
        console.error(e);
      }
    }
  }, [showAdminConfig]);

  useEffect(() => {
    setVerificationForm({
      crm:
        professionalVerification.crm ||
        userData?.crm ||
        "",
      crmState:
        professionalVerification.crmState ||
        userData?.crmState ||
        "",
      rqe:
        professionalVerification.rqe ||
        userData?.rqe ||
        "",
      specialty:
        professionalVerification.specialty ||
        userData?.specialty ||
        "",
    });
  }, [professionalVerification, userData]);

  useEffect(() => {
    if (!showAdminConfig || !isAdmin || configTab !== "verification") {
      setPendingVerificationUsers([]);
      return;
    }

    const pendingQuery = query(
      collection(db, "users"),
      where("professionalVerification.status", "==", "pending")
    );

    const unsub = onSnapshot(
      pendingQuery,
      (snap) => {
        const items = snap.docs
          .map((docSnap) => {
            const data = docSnap.data() as any;
            const verification = data?.professionalVerification || {};
            return {
              uid: docSnap.id,
              name: String(data?.profile?.name || data?.name || ""),
              email: String(data?.profile?.email || data?.email || ""),
              crm: String(verification?.crm || data?.crm || ""),
              crmState: String(verification?.crmState || data?.crmState || ""),
              rqe: String(verification?.rqe || data?.rqe || ""),
              specialty: String(
                verification?.specialty ||
                  data?.specialty ||
                  data?.profile?.specialty ||
                  ""
              ),
              method: String(verification?.method || ""),
              status: String(verification?.status || ""),
              submittedAt: verification?.submittedAt || null,
            } as PendingVerificationUser;
          })
          .sort((a, b) => {
            const aTime = typeof a.submittedAt?.toMillis === "function" ? a.submittedAt.toMillis() : 0;
            const bTime = typeof b.submittedAt?.toMillis === "function" ? b.submittedAt.toMillis() : 0;
            return bTime - aTime;
          });

        setPendingVerificationUsers(items);
      },
      (error) => {
        console.error("Erro ao carregar verificações pendentes:", error);
        setPendingVerificationUsers([]);
      }
    );

    return () => unsub();
  }, [showAdminConfig, isAdmin, configTab]);

  // Global Drag Listeners for smooth movement (Profile)
  useEffect(() => {
    const handleGlobalMove = (e: MouseEvent) => {
        // 1. Profile Photo Dragging
        if (isDraggingPhoto && photoContainerRef.current) {
            e.preventDefault();
            const deltaX = e.clientX - dragStart.x;
            const deltaY = e.clientY - dragStart.y;
            
            const containerWidth = photoContainerRef.current.offsetWidth;
            const containerHeight = photoContainerRef.current.offsetHeight;
            
            const deltaXPercent = (deltaX / containerWidth) * 100;
            const deltaYPercent = (deltaY / containerHeight) * 100;

            setEditFormData((prev: any) => ({
                ...prev,
                photoConfig: { 
                    ...prev.photoConfig, 
                    x: initialPosPercent.x + deltaXPercent, 
                    y: initialPosPercent.y + deltaYPercent 
                }
            }));
        }
    };

    const handleGlobalUp = () => {
        setIsDraggingPhoto(false);
    };

    if (isDraggingPhoto) {
        window.addEventListener('mousemove', handleGlobalMove);
        window.addEventListener('mouseup', handleGlobalUp);
    }

    return () => {
        window.removeEventListener('mousemove', handleGlobalMove);
        window.removeEventListener('mouseup', handleGlobalUp);
    };
  }, [isDraggingPhoto, dragStart, initialPosPercent]);

  const handleSaveConfig = () => {
    try {
      localStorage.setItem('orthoprecision_admin_payment', JSON.stringify(config));
      
      setShowAdminConfig(false);
      alert("Configurações salvas com sucesso!");
    } catch (e) {
      console.error(e);
      alert("Erro ao salvar.");
    }
  };

  const handleSubmitProfessionalVerification = async () => {
    const crm = verificationForm.crm.trim();
    const crmState = verificationForm.crmState.trim().toUpperCase();
    const rqe = verificationForm.rqe.trim();
    const specialty = verificationForm.specialty.trim();

    if (!crm || !crmState) {
      setVerificationError("Preencha CRM e estado para validar sua verificacao.");
      return;
    }

    try {
      setIsSubmittingVerification(true);
      setVerificationError("");

      const verificationResult = await verifyProfessionalCredentials(currentUser, {
        crm,
        crmState,
        rqe,
        specialty,
      });

      setUserData((prev: any) => ({
        ...prev,
        crm,
        crmState,
        rqe,
        specialty,
        professionalVerification: {
          ...(prev?.professionalVerification || {}),
          status: verificationResult.status,
          method: verificationResult.method || "manual_review",
          message: verificationResult.message || "",
          crm,
          crmState,
          rqe,
          specialty,
          officialName: verificationResult.officialName || "",
          officialSpecialties: verificationResult.officialSpecialties || [],
        },
      }));
      setEditFormData((prev: any) => ({
        ...prev,
        crm,
        crmState,
        rqe,
        specialty,
        professionalVerification: {
          ...(prev?.professionalVerification || {}),
          status: verificationResult.status,
          method: verificationResult.method || "manual_review",
          message: verificationResult.message || "",
          crm,
          crmState,
          rqe,
          specialty,
          officialName: verificationResult.officialName || "",
          officialSpecialties: verificationResult.officialSpecialties || [],
        },
      }));
      setVerificationNotice(
        verificationResult.message ||
          (verificationResult.status === "verified"
            ? "Verificação automática concluída."
            : "Verificacao pendente de analise manual.")
      );
      setShowProfessionalVerification(false);
    } catch (error) {
      console.error("Erro ao validar verificacao profissional:", error);
      setVerificationError(
        error instanceof Error
          ? error.message
          : "Nao foi possivel validar seu CRM agora. Tente novamente."
      );
    } finally {
      setIsSubmittingVerification(false);
    }
  };

  const handleReviewProfessionalVerification = async (
    item: PendingVerificationUser,
    status: "verified" | "rejected"
  ) => {
    try {
      setVerificationActionUid(item.uid);

      await setDoc(
        doc(db, "users", item.uid),
        {
          crm: item.crm,
          crmState: item.crmState,
          rqe: item.rqe,
          specialty: item.specialty || "",
          professionalVerification: {
            status,
            method: status === "verified" ? "manual_approved" : "manual_rejected",
            message:
              status === "verified"
                ? "Verificacao aprovada manualmente pela equipe do OrthoPrecision."
                : "Verificacao rejeitada pela equipe do OrthoPrecision.",
            crm: item.crm,
            crmState: item.crmState,
            rqe: item.rqe,
            specialty: item.specialty || "",
            verifiedAt: status === "verified" ? serverTimestamp() : null,
            reviewedAt: serverTimestamp(),
            reviewedBy: currentUser.email || currentUser.uid,
          },
          profile: {
            crm: item.crm,
            crmState: item.crmState,
            rqe: item.rqe,
            specialty: item.specialty || "",
          },
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setVerificationNotice(
        status === "verified"
          ? "Verificação profissional aprovada com sucesso."
          : "Verificação profissional rejeitada."
      );
    } catch (error) {
      console.error("Erro ao revisar verificação profissional:", error);
      setVerificationNotice("Não foi possível atualizar a verificação agora.");
    } finally {
      setVerificationActionUid(null);
    }
  };

  // ... (Existing Profile handlers: handleSaveProfile, handleProfilePhotoUpload, etc.) ...
  const handleSaveProfile = async () => {
  try {
    setUserData(editFormData);

    // salva no Firestore
    await setDoc(
      doc(db, "users", currentUser.uid),
      {
        profile: {
  name: String(editFormData?.name || ""),
  headerName: String(editFormData?.headerName || editFormData?.name || ""),
  headerTitlePrimary: String(editFormData?.headerTitlePrimary || "ORTHO PRECISION"),
  headerTitleSecondary: String(editFormData?.headerTitleSecondary || "MEDICAL CREDENTIAL"),
  bannerTagline: String(editFormData?.bannerTagline || ""),
  bannerSupportText: String(editFormData?.bannerSupportText || ""),
  bannerAccentTone: String(editFormData?.bannerAccentTone || ""),
  bannerTextColor: String(editFormData?.bannerTextColor || ""),
  bannerPrimaryColor: String(editFormData?.bannerPrimaryColor || ""),
  bannerAccentColor: String(editFormData?.bannerAccentColor || ""),
  bannerBackgroundStyle: String(editFormData?.bannerBackgroundStyle || ""),
  bannerLayoutVariant: String(editFormData?.bannerLayoutVariant || ""),
  bannerMode: String(editFormData?.bannerMode || "ai"),
  bannerTemplateId: String(editFormData?.bannerTemplateId || "premium"),
  bannerLayoutPreset: String(editFormData?.bannerLayoutPreset || "balanced"),
  bannerOffsetLeftX: Number(editFormData?.bannerOffsetLeftX ?? 0),
  bannerOffsetLeftY: Number(editFormData?.bannerOffsetLeftY ?? 0),
  bannerOffsetRightX: Number(editFormData?.bannerOffsetRightX ?? 0),
  bannerOffsetRightY: Number(editFormData?.bannerOffsetRightY ?? 0),
  crm: String(editFormData?.crm || ""),
  crmState: String(editFormData?.crmState || ""),
  rqe: String(editFormData?.rqe || ""),
  gender: String(editFormData?.gender || ""),
  hospital: String(editFormData?.hospital || ""),
  phone: String(editFormData?.phone || ""),
  email: String(editFormData?.email || ""),
  website: String(editFormData?.website || ""),
  photo: "",
  logo: String(editFormData?.logo || ""),
  banner: String(editFormData?.banner || ""),
  photoConfig: {
    x: Number(editFormData?.photoConfig?.x ?? 0),
    y: Number(editFormData?.photoConfig?.y ?? 0),
    scale: Number(editFormData?.photoConfig?.scale ?? 1),
  },
  },
        crm: String(editFormData?.crm || ""),
        crmState: String(editFormData?.crmState || ""),
        rqe: String(editFormData?.rqe || ""),
        professionalVerification: editFormData?.professionalVerification || professionalVerification,
        gender: String(editFormData?.gender || ""),
        bannerTagline: String(editFormData?.bannerTagline || ""),
        bannerSupportText: String(editFormData?.bannerSupportText || ""),
        bannerAccentTone: String(editFormData?.bannerAccentTone || ""),
        bannerTextColor: String(editFormData?.bannerTextColor || ""),
        bannerPrimaryColor: String(editFormData?.bannerPrimaryColor || ""),
        bannerAccentColor: String(editFormData?.bannerAccentColor || ""),
        bannerBackgroundStyle: String(editFormData?.bannerBackgroundStyle || ""),
        bannerLayoutVariant: String(editFormData?.bannerLayoutVariant || ""),
        bannerTemplateId: String(editFormData?.bannerTemplateId || "premium"),
        bannerLayoutPreset: String(editFormData?.bannerLayoutPreset || "balanced"),
        bannerOffsetLeftX: Number(editFormData?.bannerOffsetLeftX ?? 0),
        bannerOffsetLeftY: Number(editFormData?.bannerOffsetLeftY ?? 0),
        bannerOffsetRightX: Number(editFormData?.bannerOffsetRightX ?? 0),
        bannerOffsetRightY: Number(editFormData?.bannerOffsetRightY ?? 0),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    // continua salvando local também (backup rápido)
    const usersStr = localStorage.getItem("orthoprecision_users");
    let users = usersStr ? JSON.parse(usersStr) : [];

    const userIndex = users.findIndex(
      (u: any) => u.email === currentUser.email
    );

    if (userIndex >= 0) {
      users[userIndex] = { ...users[userIndex], ...editFormData };
    } else {
      users.push({ ...editFormData, email: currentUser.email });
    }

    localStorage.setItem("orthoprecision_users", JSON.stringify(users));
    const bannerStorageKey = `orthoprecision_doctor_banner_${currentUser?.uid || currentUser?.email || "default"}`;
    const savedBanner = String(editFormData?.banner || "").trim();
    if (savedBanner) {
      localStorage.setItem(bannerStorageKey, savedBanner);
    } else {
      localStorage.removeItem(bannerStorageKey);
    }

    localStorage.setItem(
      bannerModeStorageKey,
      String(editFormData?.bannerMode || "ai").toLowerCase() === "upload" ? "upload" : "ai"
    );

    setIsEditingProfile(false);
  } catch (error) {
    console.error("Erro ao salvar perfil:", error);
    alert("Erro ao salvar perfil.");
  }
};

  const handleQrUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setConfig(prev => ({ ...prev, pixQrCode: reader.result as string }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleProfilePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditFormData((prev: any) => ({ 
            ...prev, 
            photo: reader.result as string,
            photoConfig: { x: 0, y: 0, scale: 1 } 
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLogoUploadError("");

    if (!file.type.startsWith("image/")) {
      setLogoUploadError("Selecione uma imagem válida para a logo.");
      return;
    }

    setIsUploadingLogo(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      setEditFormData((prev: any) => ({
        ...prev,
        logo: reader.result as string,
      }));
      setIsUploadingLogo(false);
    };
    reader.onerror = () => {
      setLogoUploadError("Não foi possível carregar a logo.");
      setIsUploadingLogo(false);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setEditFormData((prev: any) => ({ ...prev, logo: "" }));
    setLogoUploadError("");
    if (logoInputRef.current) logoInputRef.current.value = "";
  };

  const handleBannerUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setBannerUploadError("");
    setBannerAiError("");
    setBannerAiOptions([]);

    if (!file.type.startsWith("image/")) {
      setBannerUploadError("Selecione uma imagem válida para o banner.");
      return;
    }

    setIsUploadingBanner(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      const bannerDataUrl = reader.result as string;
      setEditFormData((prev: any) => ({
        ...prev,
        banner: bannerDataUrl,
        bannerMode: "upload",
      }));
      const bannerStorageKey = `orthoprecision_doctor_banner_${currentUser?.uid || currentUser?.email || "default"}`;
      localStorage.setItem(bannerStorageKey, bannerDataUrl);
      localStorage.setItem(bannerModeStorageKey, "upload");
      setIsUploadingBanner(false);
    };
    reader.onerror = () => {
      setBannerUploadError("Não foi possível carregar o banner.");
      setIsUploadingBanner(false);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveBanner = () => {
    setEditFormData((prev: any) => ({ ...prev, banner: "", bannerMode: "ai" }));
    setBannerUploadError("");
    setBannerAiError("");
    setBannerAiOptions([]);
    const bannerStorageKey = `orthoprecision_doctor_banner_${currentUser?.uid || currentUser?.email || "default"}`;
    localStorage.removeItem(bannerStorageKey);
    localStorage.setItem(bannerModeStorageKey, "ai");
    if (bannerInputRef.current) bannerInputRef.current.value = "";
  };

  const applyGeneratedBanner = (option: any) => {
    if (!option) return;
    const resolvedTemplateAsset = option.imageUrl || resolveOrthoBannerTemplateAsset(option.styleTemplate || option.templateId || option.style);
    const resolvedStyleTemplate = option.styleTemplate || option.templateId || option.style || "dark-premium";

    setEditFormData((prev: any) => ({
      ...prev,
      banner: resolvedTemplateAsset,
      bannerMode: "ai",
      bannerTemplateId: resolvedStyleTemplate || prev?.bannerTemplateId || "dark-premium",
      bannerTagline: option.tagline || prev?.bannerTagline || "",
      bannerSupportText: option.supportText || prev?.bannerSupportText || "",
      bannerAccentTone: option.accentTone || prev?.bannerAccentTone || "",
      bannerTextColor: option.textColor || prev?.bannerTextColor || "",
      bannerPrimaryColor: option.primaryColor || prev?.bannerPrimaryColor || "",
      bannerAccentColor: option.accentColor || prev?.bannerAccentColor || "",
      bannerBackgroundStyle: option.backgroundSpec || option.backgroundStyle || option.styleTemplate || prev?.bannerBackgroundStyle || "",
      bannerLayoutVariant: option.layoutSpec || option.layoutVariant || prev?.bannerLayoutVariant || "",
      bannerBackgroundSpec: option.backgroundSpec || option.backgroundStyle || prev?.bannerBackgroundSpec || "",
      bannerLayoutSpec: option.layoutSpec || option.layoutVariant || prev?.bannerLayoutSpec || "",
      bannerPaletteSpec: option.paletteSpec || option.colorScheme || prev?.bannerPaletteSpec || "",
      bannerTypographySpec: option.typographySpec || prev?.bannerTypographySpec || "",
      bannerDecorativeSpec: option.decorativeSpec || prev?.bannerDecorativeSpec || "",
      headerTitlePrimary: option.headline || option.titlePrimary || prev?.headerTitlePrimary || "ORTHO PRECISION",
      headerTitleSecondary: option.subheadline || option.titleSecondary || prev?.headerTitleSecondary || "MEDICAL CREDENTIAL",
    }));

    const bannerStorageKey = `orthoprecision_doctor_banner_${currentUser?.uid || currentUser?.email || "default"}`;
    localStorage.setItem(bannerStorageKey, resolvedTemplateAsset);
    localStorage.setItem(bannerModeStorageKey, "ai");
  };

  const handleGenerateBannerWithAI = async () => {
    setBannerUploadError("");
    setBannerAiError("");
    setBannerAiOptions([]);
    setIsGeneratingBanner(true);
    setEditFormData((prev: any) => ({ ...prev, bannerMode: "ai" }));
    localStorage.setItem(bannerModeStorageKey, "ai");

    try {
      const bannerInput = {
        doctorName: String(editFormData?.name || userData?.name || currentUser.displayName || "").trim(),
        specialty: String(editFormData?.specialty || userData?.specialty || editFormData?.subspecialty || userData?.subspecialty || "").trim(),
        crm: String(editFormData?.crm || userData?.crm || "").trim(),
        crmState: String(editFormData?.crmState || userData?.crmState || "").trim(),
        hospital: String(editFormData?.hospital || userData?.hospital || "").trim(),
        phone: String(editFormData?.phone || userData?.phone || "").trim(),
        email: String(editFormData?.email || userData?.email || currentUser.email || "").trim(),
        site: String(editFormData?.website || userData?.website || "").trim(),
        displayName: String(editFormData?.headerName || userData?.headerName || editFormData?.name || userData?.name || currentUser.displayName || "").trim(),
        stylePreference: (editFormData?.bannerBackgroundStyle || userData?.bannerBackgroundStyle || editFormData?.bannerStyle || userData?.bannerStyle || "auto") as any,
        templatePreference: String(editFormData?.bannerTemplateId || editFormData?.bannerBackgroundStyle || userData?.bannerTemplateId || userData?.bannerBackgroundStyle || "dark-premium"),
        backgroundSpecPreference: String(editFormData?.bannerBackgroundSpec || userData?.bannerBackgroundSpec || "").trim(),
        layoutSpecPreference: String(editFormData?.bannerLayoutSpec || userData?.bannerLayoutSpec || "").trim(),
        paletteSpecPreference: String(editFormData?.bannerPaletteSpec || userData?.bannerPaletteSpec || "").trim(),
        typographySpecPreference: String(editFormData?.bannerTypographySpec || userData?.bannerTypographySpec || "").trim(),
        decorativeSpecPreference: String(editFormData?.bannerDecorativeSpec || userData?.bannerDecorativeSpec || "").trim(),
        bannerTagline: String(editFormData?.bannerTagline || userData?.bannerTagline || "").trim(),
        bannerSupportText: String(editFormData?.bannerSupportText || userData?.bannerSupportText || "").trim(),
        bannerPrimaryColor: String(editFormData?.bannerPrimaryColor || userData?.bannerPrimaryColor || "").trim(),
        bannerAccentColor: String(editFormData?.bannerAccentColor || userData?.bannerAccentColor || "").trim(),
        bannerBackgroundStyle: String(editFormData?.bannerBackgroundStyle || userData?.bannerBackgroundStyle || "").trim(),
        bannerLayoutVariant: String(editFormData?.bannerLayoutVariant || userData?.bannerLayoutVariant || "").trim(),
        language,
      };

      console.info("[Ortho ID Banner IA] generation start", {
        doctorName: bannerInput.doctorName,
        specialty: bannerInput.specialty,
        crm: bannerInput.crm,
        crmState: bannerInput.crmState,
        hospital: bannerInput.hospital,
        phone: bannerInput.phone,
        email: bannerInput.email,
        site: bannerInput.site,
        displayName: bannerInput.displayName,
        stylePreference: bannerInput.stylePreference,
        language: bannerInput.language,
      });

      if (!bannerInput.doctorName || !bannerInput.specialty || !bannerInput.displayName) {
        throw new Error("Preencha nome, especialidade e nome de exibição antes de gerar o banner.");
      }

      const options = await generateOrthoBannerOptions(bannerInput as any);
      console.info("[Ortho ID Banner IA] raw options received", {
        count: options.length,
        styles: options.map((option) => option.styleTemplate || option.style),
        withImage: options.filter((option) => Boolean(option.imageUrl)).length,
      });
      const normalizedOptions = options
        .map((option, index) => ({
          id: `${option.styleTemplate || option.style}-${index}`,
          ...option,
        }))
        .filter((option) => Boolean(option.imageUrl));

      if (!normalizedOptions.length) {
        throw new Error("A IA não retornou imagens válidas para o banner. Verifique a chave/API e tente novamente.");
      }

      console.info("[Ortho ID Banner IA] normalized options ready", {
        count: normalizedOptions.length,
        selected: normalizedOptions[0]?.styleTemplate || normalizedOptions[0]?.style,
      });
      setBannerAiOptions(normalizedOptions);
      applyGeneratedBanner(normalizedOptions[0]);
    } catch (error) {
      console.error("[Ortho ID Banner IA] error", error);
      setBannerAiError(
        error instanceof Error ? error.message : "Não foi possível gerar o banner com IA agora."
      );
    } finally {
      setIsGeneratingBanner(false);
    }
  };

  const handleSelectAiBanner = (option: any) => {
    applyGeneratedBanner(option);
  };

  const applyBannerLayoutPreset = (preset: 'compact' | 'balanced' | 'spacious') => {
    const defaults = {
      compact: { bannerOffsetLeftX: -4, bannerOffsetLeftY: -2, bannerOffsetRightX: 2, bannerOffsetRightY: -2 },
      balanced: { bannerOffsetLeftX: 0, bannerOffsetLeftY: 0, bannerOffsetRightX: 0, bannerOffsetRightY: 0 },
      spacious: { bannerOffsetLeftX: 3, bannerOffsetLeftY: 2, bannerOffsetRightX: 3, bannerOffsetRightY: 2 },
    }[preset];

    setEditFormData((prev: any) => ({
      ...prev,
      bannerLayoutPreset: preset,
      ...defaults,
    }));
  };

  const handleDeletePhoto = () => {
      if(window.confirm("Tem certeza que deseja remover a foto de perfil?")) {
          const resetData = { ...editFormData, photo: '', photoConfig: { x: 0, y: 0, scale: 1 } };
          setEditFormData(resetData);
          if (profileInputRef.current) profileInputRef.current.value = '';
      }
  };

  const handleAutoFaceDetect = async () => {
      if (!editFormData.photo) return;
      setIsAutoAligning(true);
      try {
          const result = await detectFaceData(editFormData.photo);
          setEditFormData((prev: any) => ({
              ...prev,
              photoConfig: { x: result.x, y: result.y, scale: result.scale }
          }));
      } catch (e) {
          alert("Não foi possível detectar o rosto automaticamente.");
      } finally {
          setIsAutoAligning(false);
      }
  };

  const handlePhotoMouseDown = (e: React.MouseEvent) => {
      if (!isEditingProfile || !editFormData.photo) return;
      e.preventDefault(); e.stopPropagation();
      setIsDraggingPhoto(true);
      setDragStart({ x: e.clientX, y: e.clientY });
      const currentConfig = editFormData.photoConfig || { x: 0, y: 0, scale: 1 };
      setInitialPosPercent({ x: currentConfig.x || 0, y: currentConfig.y || 0 });
  };

  const adjustPhotoZoom = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newScale = parseFloat(e.target.value);
      setEditFormData((prev: any) => ({ ...prev, photoConfig: { ...prev.photoConfig, scale: newScale } }));
  };

  const resetPhotoConfig = () => {
      setEditFormData((prev: any) => ({ ...prev, photoConfig: { x: 0, y: 0, scale: 1 } }));
  };

  const removeQrCode = () => {
    setConfig(prev => ({ ...prev, pixQrCode: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleGoogleDriveBackup = async () => {
    if (isBackingUp) return;
    
    setIsBackingUp(true);
    setBackupStatus('preparing');

    // 1. Collect Data
    const backupData: any = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('orthoprecision_')) {
        try {
          backupData[key] = JSON.parse(localStorage.getItem(key) || 'null');
        } catch (e) {
          backupData[key] = localStorage.getItem(key);
        }
      }
    }
    
    // Add timestamp
    backupData._backupTimestamp = new Date().toISOString();

    // 2. Try Upload
    try {
      const uploadResponse = await fetch('/api/backup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(backupData)
      });

      if (uploadResponse.ok) {
        setBackupStatus('success');
        setTimeout(() => setBackupStatus('idle'), 3000);
        alert(t('backupSuccess'));
        setIsBackingUp(false);
        return;
      }

      if (uploadResponse.status === 401) {
        // 3. Auth Flow
        setBackupStatus('authenticating');
        
        // Get Auth URL
        const urlRes = await fetch(`/api/auth/google/url?origin=${window.location.origin}`);
        const { url } = await urlRes.json();

        // Open Popup
        const width = 500;
        const height = 600;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;
        
        const popup = window.open(
          url,
          'google_auth',
          `width=${width},height=${height},top=${top},left=${left}`
        );

        // Wait for success message
        const handleMessage = async (event: MessageEvent) => {
          if (event.data?.type === 'GOOGLE_AUTH_SUCCESS') {
            window.removeEventListener('message', handleMessage);
            popup?.close();
            
            // Retry Upload
            setBackupStatus('uploading');
            const retryRes = await fetch('/api/backup', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(backupData)
            });

            if (retryRes.ok) {
              setBackupStatus('success');
              alert(t('backupSuccess'));
            } else {
              setBackupStatus('error');
              alert(t('backupAuthFailure'));
            }
            setIsBackingUp(false);
            setTimeout(() => setBackupStatus('idle'), 3000);
          }
        };

        window.addEventListener('message', handleMessage);
      } else {
        throw new Error('Upload failed');
      }

    } catch (error) {
      console.error(error);
      setBackupStatus('error');
      alert(t('backupError'));
      setIsBackingUp(false);
      setTimeout(() => setBackupStatus('idle'), 3000);
    }
  };
const handleSubmitFeedback = async () => {
  const message = feedbackText.trim();

  if (!message) {
    setFeedbackNotice("Escreva sua sugestão ou reclamação antes de enviar.");
    return;
  }

  try {
    setIsSubmittingFeedback(true);
    setFeedbackNotice(null);

    await addDoc(collection(db, "feedbacks"), {
      type: feedbackType,
      message,
      userId: currentUser.uid,
      userEmail: currentUser.email || "",
      userName:
        userData?.name ||
        currentUser.displayName ||
        currentUser.email ||
        "Usuário",
      planTier: currentPlan || "free",
      status: "new",
      createdAt: serverTimestamp(),
    });

    setFeedbackText("");
    setFeedbackType("suggestion");
    setFeedbackNotice("Feedback enviado com sucesso.");
    setShowFeedbackModal(false);
  } catch (error) {
    console.error("Erro ao enviar feedback:", error);
    setFeedbackNotice("Não foi possível enviar seu feedback agora.");
  } finally {
    setIsSubmittingFeedback(false);
  }
};
  const tabs = [
    { id: AppTab.PATIENTS, icon: Users, label: t('navPatients') },
    { id: AppTab.AGENDA, icon: Calendar, label: t('navAgenda') },
    { id: AppTab.REPORT_ANALYSIS, icon: ScanText, label: t('navReportAnalysis') },
    { id: AppTab.DECISION, icon: Activity, label: t('navProtocols') },
    { id: AppTab.ATLAS, icon: BookOpen, label: t('navAtlas') },
    { id: AppTab.CONSULT, icon: SearchCode, label: t('navConsult') },
    { id: AppTab.BUREAUCRACY, icon: FileText, label: t('navBureaucracy') },
    { id: AppTab.CLINICAL_REGISTRY, icon: BarChart3, label: t('navClinicalRegistry') },
    { id: AppTab.SUPPORT_CENTER, icon: LifeBuoy, label: t('navSupportCenter') },
  ];

  const languages = [
    { code: 'pt', label: 'PT' },
    { code: 'en', label: 'EN' },
    { code: 'es', label: 'ES' },
  ] as const;

  const ORTHO_DEFAULT_BANNER_URL = "/ortho-banner-template-premium.svg";

  const bannerSource = isEditingProfile
    ? { ...(userData || {}), ...(editFormData || {}) }
    : (userData || {});
  const bannerConfig = buildBannerConfig(bannerSource, ORTHO_DEFAULT_BANNER_URL);

  // Determine active photo and config based on edit mode
  const displayPhoto = isEditingProfile ? editFormData.photo : userData?.photo;
  const displayConfig = isEditingProfile 
      ? (editFormData.photoConfig || { x: 0, y: 0, scale: 1 }) 
      : (userData?.photoConfig || { x: 0, y: 0, scale: 1 });
  const displayLogo = isEditingProfile
    ? editFormData.logo || userData?.logo || ""
    : userData?.logo || editFormData.logo || "";
  const uploadedBannerSrc = String(bannerSource?.banner || "").trim();
  const hasUploadedBanner = Boolean(uploadedBannerSrc);
  const displayBannerMode = bannerConfig.mode === "default" ? "ai" : bannerConfig.mode;
  const previewBannerSrc = bannerConfig.bannerImageUrl;
  const displayHeaderName = bannerConfig.displayName;
  const displayHeaderHospital = bannerConfig.hospital;
  const displayHeaderPhone = bannerConfig.phone;
  const displayHeaderEmail = bannerConfig.email;
  const displayHeaderSite = bannerConfig.site;
  const profileGenderSource = String(
    (isEditingProfile ? editFormData?.gender : userData?.gender) ||
    (isEditingProfile ? editFormData?.sexo : userData?.sexo) ||
    ""
  )
    .trim()
    .toLowerCase();
  const isFemaleDoctor =
    profileGenderSource === "female" ||
    profileGenderSource === "feminino";
  const professionalPrefix = isFemaleDoctor ? "Dra." : "Dr.";
  const professionalRoleLabel = isFemaleDoctor
    ? t("orthopedicSurgeonFemale")
    : t("orthopedicSurgeonMale");
  const rawProfessionalName = String(
    (isEditingProfile ? editFormData?.name : userData?.name) || ""
  ).trim();
  const cleanProfessionalName = rawProfessionalName
    .replace(/^dr\.?\s*/i, "")
    .replace(/^dra\.?\s*/i, "")
    .trim();
  const orthoIdDisplayName = cleanProfessionalName
    ? `${professionalPrefix} ${cleanProfessionalName}`
    : rawProfessionalName || "Médico Responsável";

  const verificationStatus = isProfessionalAccessBypassed
    ? "verified"
    : professionalVerification.status;
  const verificationMethod = isProfessionalAccessBypassed
    ? "automatic"
    : professionalVerification.method;
  const showVerifiedBadge = professionalVerification.status === "verified";
  
  const professionalVerificationTitle =
    verificationStatus === "rejected"
      ? "Verificação profissional rejeitada"
      : "Verificação profissional pendente";
  const professionalVerificationDescription =
    verificationStatus === "rejected"
      ? "Revise seus dados de CRM/RQE e envie novamente para liberar os recursos clínicos do OrthoPrecision."
      : verificationStatus === "pending"
      ? "Seu CRM/RQE foi recebido e está em análise. Enquanto isso, os recursos clínicos avançados permanecem bloqueados."
      : "Para liberar todas as funcionalidades clínicas do OrthoPrecision, é necessário confirmar seu CRM/RQE. Isso garante que a plataforma seja utilizada apenas por médicos habilitados.";
  const clinicalBlockMessage =
    verificationStatus === "pending"
      ? "Sua verificação médica está em análise. Aguarde a confirmação do CRM/RQE para usar esse recurso."
      : verificationStatus === "rejected"
      ? "Sua verificação médica foi rejeitada. Atualize CRM/RQE e envie novamente para liberar esse recurso."
      : "Complete sua verificação médica com CRM/RQE para liberar esse recurso clínico.";
  const resolvedProfessionalVerificationTitle =
    verificationStatus === "verified" && verificationMethod === "automatic"
      ? "Verificacao automatica concluida"
      : verificationStatus === "rejected"
      ? "Verificação profissional rejeitada"
      : verificationStatus === "pending" && verificationMethod === "manual_review"
      ? "Verificação pendente de análise manual"
      : professionalVerificationTitle;
  const resolvedProfessionalVerificationDescription =
    verificationStatus === "verified" && verificationMethod === "automatic"
      ? "Seu CRM foi confirmado automaticamente. As funcionalidades cl?nicas completas j? est?o liberadas."
      : verificationStatus === "pending" && verificationMethod === "manual_review"
      ? "Verificação pendente de análise manual. Enquanto isso, os recursos clínicos avançados permanecem bloqueados."
      : professionalVerificationDescription;

const currentPlan = (plan?.tier || "free").toLowerCase();
const { usage, limits, remaining } = useUsageLimits(currentUser, currentPlan);

const isProPlan = currentPlan === "pro";
const isPremiumPlan = currentPlan === "premium";
const isTrialPlan = currentPlan === "trial";
const planLabel =
  currentPlan === "premium"
    ? "PREMIUM"
    : currentPlan === "pro"
    ? "PRO"
    : currentPlan === "trial"
    ? "TRIAL"
    : "FREE";

const trialDaysRemaining = (plan as any)?.trialDaysRemaining ?? null;
const usageRows = [
  {
    key: "analysesMonthly",
    label: t("usageAnalysesMonthlyLabel"),
    used: usage.analysesMonthly,
    limit: limits.analysesMonthly,
    remaining: remaining.analysesMonthly,
  },
  {
    key: "ocrMonthly",
    label: t("usageOcrMonthlyLabel"),
    used: usage.ocrMonthly,
    limit: limits.ocrMonthly,
    remaining: remaining.ocrMonthly,
  },
  {
    key: "threeDMonthly",
    label: t("usageThreeDMonthlyLabel"),
    used: usage.threeDMonthly,
    limit: limits.threeDMonthly,
    remaining: remaining.threeDMonthly,
  },
  {
    key: "patients",
    label: t("usagePatientsLabel"),
    used: usage.patients,
    limit: limits.patients,
    remaining: remaining.patients,
  },
];
/*
REGRAS DE ACESSO AOS MÓDULOS
free → só pacientes
pro → patients + decision + clinical_registry + report_analysis + consult + bureaucracy
premium → tudo liberado
*/

const hasPlanAccess = (requiredTier: "pro" | "premium") => {
  if (isPremiumPlan) return true;
  if (isTrialPlan) return true; // trial tem acesso completo (com limites)

  if (requiredTier === "pro") {
    return isProPlan;
  }

  if (requiredTier === "premium") {
    return isPremiumPlan;
  }

  return false;
};

const getSuggestedTier = (tabId: AppTab): "pro" | "premium" => {
  switch (tabId) {
    case AppTab.AGENDA:
      return "pro";

    case AppTab.DECISION:
      return "pro";

    case AppTab.CLINICAL_REGISTRY:
      return "pro";

    case AppTab.REPORT_ANALYSIS:
      return "pro";

    case AppTab.CONSULT:
      return "pro";

    case AppTab.BUREAUCRACY:
      return "pro";

    case AppTab.ATLAS:
      return "premium";

    default:
      return "pro";
  }
};

const getRequiredPlan = (tabId: AppTab) => {
  switch (tabId) {
    case AppTab.DECISION:
      return t("planBasicOrHigher");

    case AppTab.CLINICAL_REGISTRY:
      return t("planBasicOrHigher");

    case AppTab.REPORT_ANALYSIS:
      return t("planProOrHigher");

    case AppTab.CONSULT:
      return t("planProOrHigher");

    case AppTab.BUREAUCRACY:
      return t("planProOrHigher");

    case AppTab.ATLAS:
      return t("planPremium");

    default:
      return "";
  }
};

const isLockedTab = (tabId: AppTab) => {
  if (tabId === AppTab.PATIENTS) return false;
  if (isPremiumPlan) return false;
  if (isTrialPlan) return false; // trial desbloqueia todos os módulos

  return !hasPlanAccess(getSuggestedTier(tabId));
};

const allowedTabs: AppTab[] = tabs
  .filter((tab) => !isLockedTab(tab.id))
  .map((tab) => tab.id);

const getTabLockReason = (tabId: AppTab) => {
  if (!isLockedTab(tabId)) return "";
  return t("availableOnPlan", { plan: getRequiredPlan(tabId) });
};

const getLockedPlanModalContent = (tabId: AppTab | null) => {
  switch (tabId) {
    case AppTab.REPORT_ANALYSIS:
      return {
        title: "Módulo bloqueado no seu plano",
        description: "O módulo de análise de laudos está disponível no plano PRO ou superior.",
      };

    case AppTab.DECISION:
      return {
        title: "Módulo bloqueado no seu plano",
        description: "O módulo de protocolos está disponível no plano PRO ou superior.",
      };

    case AppTab.CONSULT:
      return {
        title: "Módulo bloqueado no seu plano",
        description: "O módulo de consulta inteligente está disponível no plano PRO ou superior.",
      };

    case AppTab.BUREAUCRACY:
      return {
        title: "Módulo bloqueado no seu plano",
        description: "O módulo de burocracia médica está disponível no plano PRO ou superior.",
      };

    case AppTab.ATLAS:
      return {
        title: "Módulo bloqueado no seu plano",
        description: "O módulo Plano Cirúrgico está disponível no plano PREMIUM.",
      };

    case AppTab.CLINICAL_REGISTRY:
      return {
        title: "Módulo bloqueado no seu plano",
        description: "O registro clínico está disponível no plano BASIC ou superior.",
      };

    default:
      return {
        title: "Recurso indisponível",
        description: "Este recurso não está disponível no seu plano atual.",
      };
  }
};

  return (
    <div className="flex h-screen min-w-0 overflow-hidden relative bg-[var(--op-bg-deep)] text-slate-100">

            {lockedPlanModal.open && (
        <div className="fixed inset-0 z-160 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-4xl border border-slate-700 bg-linear-to-br from-slate-900 via-slate-800 to-slate-900 p-6 shadow-[0_0_60px_rgba(15,23,42,0.6)]">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-2">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-cyan-300">
                  Plano necessário
                </p>
                <h3 className="text-2xl font-bold text-white">
                  {getLockedPlanModalContent(lockedPlanModal.tab).title}
                </h3>
                <p className="text-sm leading-relaxed text-slate-300">
                  {getLockedPlanModalContent(lockedPlanModal.tab).description}
                </p>
              </div>

              <button
                onClick={closeLockedPlanModal}
                className="rounded-full border border-slate-700 bg-slate-900/60 p-2 text-slate-300 transition-colors hover:bg-slate-800 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button
                onClick={closeLockedPlanModal}
                className="rounded-xl border border-slate-700 px-4 py-3 text-xs font-bold uppercase tracking-[0.16em] text-slate-300 transition-colors hover:bg-slate-800"
              >
                Agora não
              </button>

              <button
                onClick={() => {
                  const selectedTab = lockedPlanModal.tab;
                  closeLockedPlanModal();

                  if (selectedTab) {
                    onOpenSubscribeClick?.(getSuggestedTier(selectedTab));
                  } else {
                    onOpenSubscribeClick?.();
                  }
                }}
                className="rounded-xl border border-cyan-400/30 bg-cyan-500/15 px-4 py-3 text-xs font-bold uppercase tracking-[0.16em] text-cyan-100 transition-colors hover:bg-cyan-500/20"
              >
                Fazer upgrade
              </button>
            </div>
          </div>
        </div>
      )}
      
      
      {/* ORTHO ID MODAL (The Digital Card) */}
      {showOrthoID && userData && (
      <div className="fixed inset-0 z-150 flex items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_top,rgba(15,23,42,0.94),rgba(2,6,23,0.98))] p-3 backdrop-blur-md animate-in fade-in duration-300 max-[1199px]:items-start max-[1199px]:justify-start max-[1199px]:overflow-y-auto">
            <div className="relative mx-auto h-[calc(100dvh-56px)] w-full max-w-[1440px] perspective-1000 max-[1199px]:h-auto max-[1199px]:max-w-none">
                {/* The Card */}
                <div className="relative z-20 h-full overflow-hidden rounded-[2rem] border border-[rgba(34,211,238,0.12)] bg-[linear-gradient(180deg,rgba(15,23,42,0.82),rgba(2,6,23,0.96))] shadow-[0_25px_70px_rgba(0,0,0,0.42),inset_0_1px_0_rgba(255,255,255,0.05)]">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.08),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(37,99,235,0.08),transparent_30%)] pointer-events-none z-10" />
                    <div className="absolute -top-24 -right-24 w-48 h-48 bg-cyan-500/10 rounded-full blur-3xl" />
                    <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl" />

                    <div className="relative z-20 grid h-full min-h-0 grid-cols-1 gap-4 p-4 min-[1200px]:grid-cols-[360px_minmax(0,1fr)] min-[1200px]:gap-4 min-[1200px]:p-4">
                        {/* Header */}
                        <div className="min-[1200px]:col-span-2 relative flex items-start justify-between gap-4 pr-40">
                            <div className="flex items-center gap-2">
                                <div className="p-2.5 bg-cyan-500/10 rounded-2xl border border-cyan-400/20 shadow-[0_0_24px_rgba(34,211,238,0.08)]">
                                    <ShieldCheck className="w-8 h-8 text-cyan-300" />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h3 className="font-bold text-white tracking-[0.28em] text-[11px]">ORTHO ID</h3>
                                        {showVerifiedBadge && (
                                            <img
                                              src="/images/verified-badge.svg"
                                              alt="Médico com CRM verificado"
                                              title="Médico com CRM verificado"
                                              className="h-4 w-4"
                                            />
                                        )}
                                    </div>
                                    <p className="text-[10px] text-cyan-300/80 uppercase font-bold tracking-[0.2em]">Medical Credential</p>
                                </div>
                            </div>
                            <QrCode className="h-10 w-10 text-cyan-300/20" /> 
                        </div>

                        <div className="min-[1200px]:col-start-1 min-[1200px]:row-start-2 flex min-h-0 flex-col gap-4 overflow-hidden">
                            <div className="text-center py-1">
                                <div className="flex flex-col items-center">
                                    <div className="relative mx-auto mb-3 h-28 w-28 rounded-full border border-cyan-400/15 bg-[linear-gradient(180deg,rgba(15,23,42,0.95),rgba(2,6,23,0.95))] p-1.5 shadow-[0_25px_60px_rgba(0,0,0,0.35),0_0_24px_rgba(34,211,238,0.08)] sm:h-[100px] sm:w-[100px]">
                                        <div 
                                            ref={photoContainerRef}
                                            className={`w-full h-full rounded-full bg-slate-900 overflow-hidden relative ${isEditingProfile && displayPhoto ? 'cursor-move' : ''}`}
                                            onMouseDown={handlePhotoMouseDown}
                                        >
                                            {displayPhoto ? (
                                                <img 
                                                    src={displayPhoto} 
                                                    alt="Profile" 
                                                    className={`w-full h-full pointer-events-none select-none object-cover`}
                                                    style={{
                                                        transform: `translate(${displayConfig.x}%, ${displayConfig.y}%) scale(${displayConfig.scale})`,
                                                        transition: isDraggingPhoto ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                                    }}
                                                    draggable={false}
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <span className="text-4xl font-bold text-slate-500">{editFormData.name?.substring(0,2).toUpperCase()}</span>
                                                </div>
                                            )}
                                            
                                            {isEditingProfile && !displayPhoto && (
                                                <div 
                                                    onClick={() => profileInputRef.current?.click()}
                                                    className="absolute inset-0 bg-black/50 flex items-center justify-center cursor-pointer hover:bg-black/70 transition-all"
                                                >
                                                    <Camera className="w-8 h-8 text-white" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="absolute bottom-0 right-0 z-10 flex h-7 w-7 items-center justify-center rounded-full border-4 border-slate-900 bg-emerald-500 shadow-[0_0_18px_rgba(16,185,129,0.32)] pointer-events-none">
                                            <Check className="h-3.5 w-3.5 text-white" />
                                        </div>
                                    </div>

                                    {isEditingProfile && (
                                        <div className="mb-2 flex w-full max-w-56 flex-col gap-2 rounded-2xl border border-[rgba(34,211,238,0.10)] bg-[rgba(15,23,42,0.72)] p-2.5 backdrop-blur-sm shadow-[0_18px_45px_rgba(0,0,0,0.24)] animate-in slide-in-from-top-2">
                                            <div className="flex items-center justify-between gap-2">
                                                <button onClick={() => profileInputRef.current?.click()} className="p-2 hover:bg-cyan-500/10 rounded-xl text-cyan-300 transition-colors bg-slate-900/50 border border-slate-700/60" title={t("changePhoto")}>
                                                    <Camera className="w-4 h-4" />
                                                </button>
                                                {displayPhoto && (
                                                    <>
                                                        <button onClick={handleAutoFaceDetect} disabled={isAutoAligning} className="p-2 hover:bg-cyan-500/10 text-cyan-300 rounded-xl transition-colors bg-slate-900/50 border border-slate-700/60" title={t("autoAdjustAi")}>
                                                            <Sparkles className={`w-4 h-4 ${isAutoAligning ? 'animate-spin' : ''}`} />
                                                        </button>
                                                        <button onClick={resetPhotoConfig} className="p-2 hover:bg-slate-700 rounded-xl text-slate-300 transition-colors bg-slate-900/50 border border-slate-700/60" title={t("resetPosition")}>
                                                            <RotateCcw className="w-4 h-4" />
                                                        </button>
                                                        <button onClick={handleDeletePhoto} className="p-2 hover:bg-red-500/20 text-red-400 rounded-xl transition-colors bg-slate-900/50 border border-slate-700/60" title={t("deletePhoto")}>
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                            
                                            {displayPhoto && (
                                                <div className="flex items-center gap-2 px-1">
                                                    <ZoomOut className="w-3 h-3 text-slate-500" />
                                                    <input 
                                                        type="range" 
                                                        min="0.5" 
                                                        max="3" 
                                                        step="0.1" 
                                                        value={displayConfig.scale} 
                                                        onChange={adjustPhotoZoom}
                                                        className="w-full h-1 bg-slate-600 rounded-lg appearance-none cursor-pointer accent-cyan-400"
                                                    />
                                                    <ZoomIn className="w-3 h-3 text-slate-500" />
                                                </div>
                                            )}
                                            
                                            {displayPhoto && <p className="text-[9px] text-slate-500 font-medium">Arraste a foto para ajustar</p>}
                                        </div>
                                    )}
                                </div>
                                {isEditingProfile ? (
                                    <input 
                                      type="text" 
                                      value={editFormData.name} 
                                      onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                                    className="w-full rounded-xl border border-slate-700/70 bg-slate-950/80 px-3 py-2 text-center text-[13px] font-bold text-white outline-none transition-all focus:border-cyan-400 focus:shadow-[0_0_0_2px_rgba(34,211,238,0.12)] mb-1"
                                      placeholder="Nome Completo"
                                    />
                                ) : (
                                    <h2 className="mb-1 text-[18px] font-bold tracking-tight text-white">{orthoIdDisplayName}</h2>
                                )}
                                <p className="text-[12px] font-medium text-slate-400">{professionalRoleLabel}</p>
                                <p className="mt-3 inline-flex items-center justify-center rounded-full border border-emerald-400/35 bg-emerald-500/12 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-300 shadow-[0_0_18px_rgba(16,185,129,0.12)]">
                                  Conta ativa
                                </p>
                            </div>

                            <div className="min-h-0 space-y-3 overflow-hidden rounded-2xl border border-[rgba(34,211,238,0.10)] bg-[rgba(2,6,23,0.58)] p-4 backdrop-blur-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    <div>
                                        <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-300/80">CRM / UF</p>
                                        {isEditingProfile ? (
                                            <input 
                                              type="text" 
                                              value={editFormData.crm} 
                                              onChange={(e) => setEditFormData({...editFormData, crm: e.target.value})}
                                              className="w-full h-10 rounded-[10px] border border-slate-700/70 bg-slate-950/80 px-3 text-[12px] font-mono text-white outline-none transition-all focus:border-cyan-400 focus:shadow-[0_0_0_2px_rgba(34,211,238,0.12)]"
                                            />
                                        ) : (
                                            <p className="text-[13px] font-mono font-bold tracking-wide text-white">{userData.crm || 'N/A'}</p>
                                        )}
                                    </div>
                                    <div>
                                        <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-300/80">RQE</p>
                                        {isEditingProfile ? (
                                            <input 
                                              type="text" 
                                              value={editFormData.rqe} 
                                              onChange={(e) => setEditFormData({...editFormData, rqe: e.target.value})}
                                              className="w-full h-10 rounded-[10px] border border-slate-700/70 bg-slate-950/80 px-3 text-[12px] font-mono text-white outline-none transition-all focus:border-cyan-400 focus:shadow-[0_0_0_2px_rgba(34,211,238,0.12)]"
                                            />
                                        ) : (
                                            <p className="text-[13px] font-mono font-bold tracking-wide text-white">{userData.rqe || 'N/A'}</p>
                                        )}
                                    </div>
                                </div>
                                
                                <div className="border-t border-slate-800/80 pt-3">
                                    <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-300/80">{t("genderLabel")}</p>
                                    {isEditingProfile ? (
                                        <select
                                          value={editFormData.gender || ""}
                                          onChange={(e) => setEditFormData({...editFormData, gender: e.target.value})}
                                          className="w-full h-10 rounded-[10px] border border-slate-700/70 bg-slate-950/80 px-3 text-[12px] text-white outline-none transition-all focus:border-cyan-400 focus:shadow-[0_0_0_2px_rgba(34,211,238,0.12)]"
                                        >
                                          <option value="">{t("genderRequiredPlaceholder")}</option>
                                          <option value="male">{t("genderMale")}</option>
                                          <option value="female">{t("genderFemale")}</option>
                                        </select>
                                    ) : (
                                        <p className="text-[13px] text-slate-300">
                                          {profileGenderSource === "female" || profileGenderSource === "feminino"
                                            ? t("genderFemale")
                                            : profileGenderSource === "male" || profileGenderSource === "masculino"
                                            ? t("genderMale")
                                            : "N/A"}
                                        </p>
                                    )}
                                </div>

                                <div className="border-t border-slate-800/80 pt-3">
                                    <p className="mb-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-300/80"><Building2 className="h-3 w-3"/> Afiliação</p>
                                    {isEditingProfile ? (
                                        <input 
                                          type="text" 
                                          value={editFormData.hospital} 
                                          onChange={(e) => setEditFormData({...editFormData, hospital: e.target.value})}
                                          className="w-full h-10 rounded-[10px] border border-slate-700/70 bg-slate-950/80 px-3 text-[12px] text-white outline-none transition-all focus:border-cyan-400 focus:shadow-[0_0_0_2px_rgba(34,211,238,0.12)]"
                                          placeholder="Hospital"
                                        />
                                    ) : (
                                        <p className="text-[13px] text-slate-300">{userData.hospital}</p>
                                    )}
                                </div>
                                
                                <div className="grid grid-cols-1 gap-2 border-t border-slate-800/80 pt-3">
                                    <div className="flex items-center gap-2">
                                        <Phone className="h-3 w-3 shrink-0 text-cyan-300/80"/>
                                        {isEditingProfile ? (
                                            <input 
                                              type="text" 
                                              value={editFormData.phone} 
                                              onChange={(e) => setEditFormData({...editFormData, phone: e.target.value})}
                                              className="flex-1 h-10 rounded-[10px] border border-slate-700/70 bg-slate-950/80 px-3 text-[12px] text-slate-300 outline-none transition-all focus:border-cyan-400 focus:shadow-[0_0_0_2px_rgba(34,211,238,0.12)]"
                                              placeholder="Telefone"
                                            />
                                        ) : (
                                            <p className="text-xs text-slate-400">{userData.phone || 'N/A'}</p>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Mail className="h-3 w-3 shrink-0 text-cyan-300/80"/>
                                        {isEditingProfile ? (
                                            <input 
                                              type="text" 
                                              value={editFormData.email} 
                                              onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                                              className="flex-1 h-10 rounded-[10px] border border-slate-700/70 bg-slate-950/80 px-3 text-[12px] text-slate-300 outline-none transition-all focus:border-cyan-400 focus:shadow-[0_0_0_2px_rgba(34,211,238,0.12)]"
                                              placeholder="Email"
                                            />
                                        ) : (
                                            <p className="text-xs text-slate-400">{userData.email || 'N/A'}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="min-[1200px]:col-start-2 min-[1200px]:row-start-2 flex min-h-0 flex-col gap-3 overflow-hidden min-[1200px]:overflow-y-auto min-[1200px]:pr-1 custom-scrollbar">
                            <div className="flex items-center justify-end gap-2">
                                {!isEditingProfile ? (
                                    <button onClick={() => setIsEditingProfile(true)} className="p-2.5 bg-[rgba(15,23,42,0.72)] rounded-full hover:bg-cyan-500/10 text-cyan-300 transition-colors border border-slate-700/70 shadow-[0_8px_24px_rgba(0,0,0,0.18)]">
                                        <Pen className="w-6 h-6" />
                                    </button>
                                ) : (
                                    <button onClick={handleSaveProfile} className="p-2.5 bg-emerald-600 rounded-full hover:bg-emerald-500 text-white transition-colors shadow-[0_10px_28px_rgba(16,185,129,0.26)]">
                                        <Save className="w-6 h-6" />
                                    </button>
                                )}
                                <button onClick={() => { setShowOrthoID(false); setIsEditingProfile(false); }} className="p-2.5 bg-[rgba(15,23,42,0.72)] rounded-full hover:bg-cyan-500/10 text-white transition-colors border border-slate-700/70 shadow-[0_8px_24px_rgba(0,0,0,0.18)]">
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            <section className="w-full rounded-[20px] border border-[rgba(34,211,238,0.10)] bg-[linear-gradient(180deg,rgba(15,23,42,0.72),rgba(2,6,23,0.88))] p-5 text-left shadow-[0_18px_45px_rgba(0,0,0,0.24),inset_0_1px_0_rgba(255,255,255,0.04)]">
                                <div className="flex items-start gap-3">
                                    <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-cyan-400/20 bg-cyan-500/10 text-cyan-300 shadow-[0_0_18px_rgba(34,211,238,0.08)]">
                                        <Palette className="h-4.5 w-4.5" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-cyan-300/80">
                                            {t('orthoIdPdfBrandingSection')}
                                        </p>
                                        <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
                                            {t('orthoIdPdfBrandingSubtitle')}
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-5">
                                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                                        <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400">
                                            {t('orthoIdBannerModeLabel')}
                                        </p>
                                        <div className="flex flex-wrap gap-2 justify-end">
                                            <button
                                                type="button"
                                                onClick={handleGenerateBannerWithAI}
                                                disabled={isGeneratingBanner}
                                                className={`inline-flex items-center gap-2 rounded-full border px-3 py-2.5 text-[10px] font-bold uppercase tracking-[0.16em] transition-all shadow-[0_10px_30px_rgba(34,211,238,0.12)] hover:-translate-y-0.5 ${
                                                    displayBannerMode === 'ai'
                                                        ? 'border-cyan-400 bg-cyan-500/15 text-cyan-200'
                                                        : 'border-slate-700 bg-slate-900 text-slate-400 hover:border-cyan-400/30 hover:text-white'
                                                } ${isGeneratingBanner ? 'cursor-wait opacity-70' : ''}`}
                                            >
                                                {isGeneratingBanner
                                                    ? (language === 'en' ? 'Generating...' : language === 'es' ? 'Generando...' : 'Gerando...')
                                                    : t('orthoIdBannerModeAi')}
                                                <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[9px] text-emerald-300">
                                                    {t('orthoIdBannerModeRecommended')}
                                                </span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setBannerAiError("");
                                                    setBannerAiOptions([]);
                                                    setEditFormData((prev: any) => ({ ...prev, bannerMode: 'upload' }));
                                                    localStorage.setItem(bannerModeStorageKey, 'upload');
                                                }}
                                                className={`inline-flex items-center gap-2 rounded-full border px-3 py-2.5 text-[10px] font-bold uppercase tracking-[0.16em] transition-all ${
                                                    displayBannerMode === 'upload'
                                                        ? 'border-cyan-400 bg-cyan-500/15 text-cyan-200'
                                                        : 'border-slate-700 bg-slate-900 text-slate-400 hover:border-cyan-400/30 hover:text-white'
                                                }`}
                                            >
                                                {t('orthoIdBannerModeUpload')}
                                            </button>
                                        </div>
                                    </div>
                                    {isEditingProfile && (
                                    <div className="mb-4 rounded-2xl border border-slate-700/70 bg-slate-950/35 p-4">
                                        <div className="mb-3 flex items-center justify-between gap-2">
                                                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-cyan-300/80">
                                                    Ajuste do banner
                                                </p>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {(['compact', 'balanced', 'spacious'] as const).map((preset) => (
                                                        <button
                                                            key={preset}
                                                            type="button"
                                                            onClick={() => applyBannerLayoutPreset(preset)}
                                                            className={`rounded-full border px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.18em] transition-colors ${
                                                                editFormData.bannerLayoutPreset === preset
                                                                    ? 'border-cyan-400 bg-cyan-500/15 text-cyan-200 shadow-[0_0_16px_rgba(34,211,238,0.10)]'
                                                                    : 'border-slate-700 bg-slate-900 text-slate-400 hover:border-slate-500 hover:text-white'
                                                            }`}
                                                        >
                                                            {preset === 'compact' ? 'Compacto' : preset === 'spacious' ? 'Espaçado' : 'Equilibrado'}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-1 gap-2 min-[1366px]:grid-cols-2">
                                                {[
                                                    {
                                                        key: 'bannerOffsetLeftY',
                                                        label: 'Bloco esquerdo Y',
                                                        value: Number(editFormData.bannerOffsetLeftY ?? 0),
                                                    },
                                                    {
                                                        key: 'bannerOffsetRightY',
                                                        label: 'Bloco direito Y',
                                                        value: Number(editFormData.bannerOffsetRightY ?? 0),
                                                    },
                                                    {
                                                        key: 'bannerOffsetLeftX',
                                                        label: 'Bloco esquerdo X',
                                                        value: Number(editFormData.bannerOffsetLeftX ?? 0),
                                                    },
                                                    {
                                                        key: 'bannerOffsetRightX',
                                                        label: 'Bloco direito X',
                                                        value: Number(editFormData.bannerOffsetRightX ?? 0),
                                                    },
                                                ].map((item) => (
                                                    <label key={item.key} className="space-y-1">
                                                        <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                                                            <span>{item.label}</span>
                                                            <span className="text-slate-300">{item.value}</span>
                                                        </div>
                                                        <input
                                                            type="range"
                                                            min={item.key.includes('X') ? -10 : -12}
                                                            max={item.key.includes('X') ? 10 : 12}
                                                            step="1"
                                                            value={item.value}
                                                            onChange={(e) =>
                                                                setEditFormData((prev: any) => ({
                                                                    ...prev,
                                                                    [item.key]: Number(e.target.value),
                                                                }))
                                                            }
                                                            className="h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-800 accent-cyan-400"
                                                        />
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400">
                                        {t('orthoIdBannerPreviewLabel')}
                                    </p>
                                    <div className="aspect-[16/5] min-h-[300px] max-h-[370px] overflow-y-auto rounded-[16px] border border-slate-700/70 bg-slate-950/50 p-2 shadow-[0_30px_80px_rgba(0,0,0,0.4)] custom-scrollbar">
                                        <PdfHeaderPreview
                                            backgroundSrc={previewBannerSrc}
                                            bannerConfig={bannerConfig}
                                        />
                                    </div>
                                    {displayBannerMode === 'ai' && bannerAiOptions.length > 0 && (
                                        <div className="mt-4 grid grid-cols-1 gap-3 min-[1366px]:grid-cols-2">
                                            {bannerAiOptions.map((option: any) => {
                                                const isSelected = String(bannerConfig.bannerImageUrl || '') === String(option.imageUrl || '');
                                                  const optionConfig = {
                                                    ...bannerConfig,
                                                    mode: 'ai' as const,
                                                    templateId: option.styleTemplate || option.templateId || bannerConfig.templateId,
                                                    title: option.headline || option.titlePrimary || bannerConfig.title,
                                                    subtitle: option.subheadline || option.titleSecondary || bannerConfig.subtitle,
                                                    tagline: option.tagline || bannerConfig.tagline,
                                                    supportText: option.supportText || bannerConfig.supportText,
                                                    accentTone: option.accentTone || bannerConfig.accentTone,
                                                    textColor: option.textColor || bannerConfig.textColor,
                                                    primaryColor: option.primaryColor || bannerConfig.primaryColor,
                                                    accentColor: option.accentColor || bannerConfig.accentColor,
                                                    backgroundStyle: option.backgroundSpec || option.backgroundStyle || option.styleTemplate || bannerConfig.backgroundStyle,
                                                    backgroundSpec: option.backgroundSpec || option.backgroundStyle || bannerConfig.backgroundSpec,
                                                    layoutVariant: option.layoutSpec || option.layoutVariant || bannerConfig.layoutVariant,
                                                    layoutSpec: option.layoutSpec || option.layoutVariant || bannerConfig.layoutSpec,
                                                    paletteSpec: option.paletteSpec || option.colorScheme || bannerConfig.paletteSpec,
                                                    typographySpec: option.typographySpec || bannerConfig.typographySpec,
                                                    decorativeSpec: option.decorativeSpec || bannerConfig.decorativeSpec,
                                                    bannerImageUrl: option.imageUrl || bannerConfig.bannerImageUrl,
                                                };

                                                return (
                                                    <button
                                                        key={option.id}
                                                        type="button"
                                                        onClick={() => handleSelectAiBanner(option)}
                                                    className={`overflow-hidden rounded-[16px] border text-left transition-all ${
                                                        isSelected
                                                            ? 'border-cyan-400 shadow-[0_0_0_1px_rgba(34,211,238,0.08),0_18px_45px_rgba(0,0,0,0.28)]'
                                                            : 'border-slate-700 hover:border-slate-500'
                                                    }`}
                                                >
                                                        <div className="p-1.5">
                                                            <PdfHeaderPreview
                                                                backgroundSrc={option.imageUrl || previewBannerSrc}
                                                                compact
                                                                bannerConfig={optionConfig}
                                                            />
                                                        </div>
                                                        <div className="border-t border-slate-800 px-3 py-1.5">
                                                            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-300">
                                                            {option.styleTemplate || option.style}
                                                        </p>
                                                        <p className="mt-1 text-[11px] text-slate-500">
                                                                {option.headline || option.titlePrimary} • {option.subheadline || option.titleSecondary}
                                                            </p>
                                                            {option.tagline && (
                                                                <p className="mt-1 text-[10px] leading-snug text-slate-400">
                                                                    {option.tagline}
                                                                </p>
                                                            )}
                                                            {option.tone && (
                                                                <p className="mt-1 text-[10px] leading-snug text-cyan-200/80">
                                                                    {option.tone}
                                                                </p>
                                                            )}
                                                            {option.supportText && (
                                                                <p className="mt-1 text-[10px] leading-snug text-cyan-200/80">
                                                                    {option.supportText}
                                                                </p>
                                                            )}
                                                            {(option.backgroundStyle || option.layoutVariant) && (
                                                                <p className="mt-1 text-[9px] uppercase tracking-[0.18em] text-slate-500">
                                                                    {[option.backgroundStyle, option.layoutVariant].filter(Boolean).join(' • ')}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {isEditingProfile && (
                                        <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
                                        <button
                                            type="button"
                                            onClick={() => bannerInputRef.current?.click()}
                                            disabled={isUploadingBanner}
                                            className="inline-flex items-center justify-center gap-2 rounded-xl border border-cyan-400/20 bg-[linear-gradient(135deg,#22d3ee,#2563eb)] px-3 py-2.5 text-[11px] font-bold uppercase tracking-[0.16em] text-white shadow-[0_10px_30px_rgba(34,211,238,0.18)] transition-all hover:-translate-y-0.5 hover:shadow-[0_14px_36px_rgba(34,211,238,0.26)] disabled:cursor-not-allowed disabled:opacity-70"
                                        >
                                            <Upload className="h-3.5 w-3.5" />
                                            {hasUploadedBanner ? t('orthoIdPdfReplaceBanner') : t('orthoIdPdfUploadBanner')}
                                        </button>

                                        {hasUploadedBanner && (
                                            <button
                                                type="button"
                                                onClick={handleRemoveBanner}
                                                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-700/70 bg-slate-900/70 px-3 py-2.5 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-300 transition-colors hover:border-cyan-400/30 hover:bg-cyan-500/10 hover:text-white"
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                                {t('orthoIdPdfRemoveBanner')}
                                            </button>
                                        )}
                                    </div>
                                )}

                                {isEditingProfile ? (
                                    <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 min-[1366px]:grid-cols-3">
                                        <div className="space-y-1">
                                            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                                                Título principal do banner
                                            </p>
                                            <input
                                                type="text"
                                                value={editFormData.headerTitlePrimary || ''}
                                                onChange={(e) => setEditFormData({ ...editFormData, headerTitlePrimary: e.target.value })}
                                                className="w-full h-10 rounded-[10px] border border-slate-700/70 bg-slate-950/80 px-3 text-[12px] text-white outline-none transition-all focus:border-cyan-400 focus:shadow-[0_0_0_2px_rgba(34,211,238,0.12)]"
                                                placeholder="ORTHO PRECISION"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                                                Subtítulo do banner
                                            </p>
                                            <input
                                                type="text"
                                                value={editFormData.headerTitleSecondary || ''}
                                                onChange={(e) => setEditFormData({ ...editFormData, headerTitleSecondary: e.target.value })}
                                                className="w-full h-10 rounded-[10px] border border-slate-700/70 bg-slate-950/80 px-3 text-[12px] text-white outline-none transition-all focus:border-cyan-400 focus:shadow-[0_0_0_2px_rgba(34,211,238,0.12)]"
                                                placeholder="MEDICAL CREDENTIAL"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                                                {t('orthoIdPdfHeaderNameLabel')}
                                            </p>
                                            <input
                                                type="text"
                                                value={editFormData.headerName || ''}
                                                onChange={(e) => setEditFormData({ ...editFormData, headerName: e.target.value })}
                                                className="w-full h-10 rounded-[10px] border border-slate-700/70 bg-slate-950/80 px-3 text-[12px] text-white outline-none transition-all focus:border-cyan-400 focus:shadow-[0_0_0_2px_rgba(34,211,238,0.12)]"
                                                placeholder="OrthoPrecision"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                                                {t('orthoIdPdfDoctorNameLabel')}
                                            </p>
                                            <input
                                                type="text"
                                                value={editFormData.name || ''}
                                                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                                                className="w-full h-10 rounded-[10px] border border-slate-700/70 bg-slate-950/80 px-3 text-[12px] text-white outline-none transition-all focus:border-cyan-400 focus:shadow-[0_0_0_2px_rgba(34,211,238,0.12)]"
                                                placeholder="Nome do médico"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                                                {t('orthoIdPdfInstitutionLabel')}
                                            </p>
                                            <input
                                                type="text"
                                                value={editFormData.hospital || ''}
                                                onChange={(e) => setEditFormData({ ...editFormData, hospital: e.target.value })}
                                                className="w-full h-10 rounded-[10px] border border-slate-700/70 bg-slate-950/80 px-3 text-[12px] text-white outline-none transition-all focus:border-cyan-400 focus:shadow-[0_0_0_2px_rgba(34,211,238,0.12)]"
                                                placeholder="Hospital / clínica"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                                                {t('orthoIdPdfPhoneLabel')}
                                            </p>
                                            <input
                                                type="text"
                                                value={editFormData.phone || ''}
                                                onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                                                className="w-full h-10 rounded-[10px] border border-slate-700/70 bg-slate-950/80 px-3 text-[12px] text-white outline-none transition-all focus:border-cyan-400 focus:shadow-[0_0_0_2px_rgba(34,211,238,0.12)]"
                                                placeholder="Telefone"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                                                {t('orthoIdPdfEmailLabel')}
                                            </p>
                                            <input
                                                type="text"
                                                value={editFormData.email || ''}
                                                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                                                className="w-full h-10 rounded-[10px] border border-slate-700/70 bg-slate-950/80 px-3 text-[12px] text-white outline-none transition-all focus:border-cyan-400 focus:shadow-[0_0_0_2px_rgba(34,211,238,0.12)]"
                                                placeholder="E-mail"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                                                {t('orthoIdPdfWebsiteLabel')}
                                            </p>
                                            <input
                                                type="text"
                                                value={editFormData.website || ''}
                                                onChange={(e) => setEditFormData({ ...editFormData, website: e.target.value })}
                                                className="w-full h-10 rounded-[10px] border border-slate-700/70 bg-slate-950/80 px-3 text-[12px] text-white outline-none transition-all focus:border-cyan-400 focus:shadow-[0_0_0_2px_rgba(34,211,238,0.12)]"
                                                placeholder="Site"
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="mt-3 grid grid-cols-1 gap-2 text-[12px] text-slate-300 sm:grid-cols-2 min-[1366px]:grid-cols-3">
                                        <p className="rounded-xl border border-slate-700/60 bg-slate-900/50 px-3 py-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                                            <span className="font-bold text-slate-500">{t('orthoIdPdfHeaderNameLabel')}:</span> {displayHeaderName || ''}
                                        </p>
                                        <p className="rounded-xl border border-slate-700/60 bg-slate-900/50 px-3 py-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                                            <span className="font-bold text-slate-500">{t('orthoIdPdfDoctorNameLabel')}:</span> {bannerConfig.doctorName || ''}
                                        </p>
                                        <p className="rounded-xl border border-slate-700/60 bg-slate-900/50 px-3 py-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                                            <span className="font-bold text-slate-500">{t('orthoIdPdfInstitutionLabel')}:</span> {displayHeaderHospital || ''}
                                        </p>
                                        <p className="rounded-xl border border-slate-700/60 bg-slate-900/50 px-3 py-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                                            <span className="font-bold text-slate-500">{t('orthoIdPdfPhoneLabel')}:</span> {displayHeaderPhone || ''}
                                        </p>
                                        <p className="rounded-xl border border-slate-700/60 bg-slate-900/50 px-3 py-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                                            <span className="font-bold text-slate-500">{t('orthoIdPdfEmailLabel')}:</span> {displayHeaderEmail || ''}
                                        </p>
                                        <p className="rounded-xl border border-slate-700/60 bg-slate-900/50 px-3 py-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                                            <span className="font-bold text-slate-500">{t('orthoIdPdfWebsiteLabel')}:</span> {displayHeaderSite || ''}
                                        </p>
                                    </div>
                                )}

                                {bannerAiError && (
                                    <p className="mt-2 text-[11px] text-red-400">{bannerAiError}</p>
                                )}
                                {isEditingProfile && bannerUploadError && (
                                    <p className="mt-2 text-[11px] text-red-400">{bannerUploadError}</p>
                                )}
                            </section>
                        </div>

                        {/* Footer Status */}
                        <div className="min-[1200px]:col-span-2 min-[1200px]:row-start-4 flex items-center justify-between pt-1">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_12px_rgba(16,185,129,0.4)]"></div>
                                <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-300">Active License</span>
                            </div>
                            <span className="text-[9px] text-slate-600 font-mono">ID: {Math.random().toString(36).substr(2, 9).toUpperCase()}</span>
                        </div>

                        <input type="file" ref={profileInputRef} className="hidden" accept="image/*" onChange={handleProfilePhotoUpload} />
                        <input type="file" ref={logoInputRef} className="hidden" accept="image/*" onChange={handleLogoUpload} />
                        <input type="file" ref={bannerInputRef} className="hidden" accept="image/*" onChange={handleBannerUpload} />
                    </div>
                </div>
            </div>
        </div>
      )}

      {/* Admin Config Modal */}
      {showAdminConfig && isAdmin && (
        <div className="fixed inset-0 z-100 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-300">
           <div className="glass w-full max-w-2xl rounded-3xl border border-slate-700 shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
              <div className="p-6 border-b border-slate-700 flex justify-between items-center bg-slate-900/50">
                 <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <Settings className="w-6 h-6 text-emerald-400" />
                    Painel do Administrador
                 </h3>
                 <button onClick={() => setShowAdminConfig(false)} className="p-2 hover:bg-slate-800 rounded-full transition-colors">
                    <X className="w-5 h-5 text-slate-400" />
                 </button>
              </div>
              
              <div className="flex p-4 gap-2 bg-slate-900/30">
                 <button 
                   onClick={() => setConfigTab('pix')}
                   className={`flex-1 py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${configTab === 'pix' ? 'bg-emerald-600 text-white shadow-lg' : 'bg-slate-800 text-slate-400 hover:text-white'}`}
                 >
                    <QrCode className="w-4 h-4" /> Pix
                 </button>
                 <button 
                   onClick={() => setConfigTab('cc')}
                   className={`flex-1 py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${configTab === 'cc' ? 'bg-blue-600 text-white shadow-lg' : 'bg-slate-800 text-slate-400 hover:text-white'}`}
                 >
                    <CreditCard className="w-4 h-4" /> Cartão
                 </button>
                 <button 
                   onClick={() => setConfigTab('verification')}
                   className={`flex-1 py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${configTab === 'verification' ? 'bg-amber-600 text-white shadow-lg' : 'bg-slate-800 text-slate-400 hover:text-white'}`}
                 >
                    <ShieldCheck className="w-4 h-4" /> Verificação
                 </button>
              </div>

              <div className="p-6 overflow-y-auto custom-scrollbar flex-1">
                 {configTab === 'pix' ? (
                    <div className="space-y-4 animate-in slide-in-from-left-4">
                       <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                             <label className="text-xs font-bold text-slate-400 uppercase">Chave Pix</label>
                             <input type="text" value={config.pixKey} onChange={e => setConfig({...config, pixKey: e.target.value})} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 outline-none" placeholder="Email, CPF, Telefone..." />
                          </div>
                          <div className="space-y-2">
                             <label className="text-xs font-bold text-slate-400 uppercase">Nome Beneficiário</label>
                             <input type="text" value={config.pixBeneficiary} onChange={e => setConfig({...config, pixBeneficiary: e.target.value})} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 outline-none" placeholder="Nome Completo" />
                          </div>
                          <div className="space-y-2">
                             <label className="text-xs font-bold text-slate-400 uppercase">Banco</label>
                             <input type="text" value={config.pixBank} onChange={e => setConfig({...config, pixBank: e.target.value})} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 outline-none" placeholder="Instituição Financeira" />
                          </div>
                          <div className="space-y-2">
                             <label className="text-xs font-bold text-slate-400 uppercase">CPF/CNPJ</label>
                             <input type="text" value={config.pixCpf} onChange={e => setConfig({...config, pixCpf: e.target.value})} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 outline-none" placeholder="Documento" />
                          </div>
                       </div>
                       
                       <div className="pt-4 border-t border-slate-700">
                          <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Imagem do QR Code</label>
                          <div className="flex items-start gap-4">
                             <div className="w-32 h-32 bg-white rounded-xl flex items-center justify-center overflow-hidden border-2 border-slate-700">
                                {config.pixQrCode ? (
                                   <img src={config.pixQrCode} alt="QR" className="w-full h-full object-contain" />
                                ) : (
                                   <QrCode className="w-12 h-12 text-slate-300" />
                                )}
                             </div>
                             <div className="flex flex-col gap-2">
                                <button onClick={() => fileInputRef.current?.click()} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-xs font-bold flex items-center gap-2 border border-slate-600">
                                   <Upload className="w-4 h-4" /> Carregar Imagem
                                </button>
                                {config.pixQrCode && (
                                   <button onClick={removeQrCode} className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg text-xs font-bold flex items-center gap-2 border border-red-500/20">
                                      <Trash2 className="w-4 h-4" /> Remover
                                   </button>
                                )}
                                <p className="text-[10px] text-slate-500 max-w-37.5">Use uma imagem quadrada, clara e de baixa resolução para economizar espaço.</p>
                                <input type="file" ref={fileInputRef} onChange={handleQrUpload} accept="image/*" className="hidden" />
                             </div>
                          </div>
                       </div>
                    </div>
                 ) : configTab === 'cc' ? (
                    <div className="space-y-4 animate-in slide-in-from-right-4">
                       <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl text-xs text-blue-300 mb-4">
                          Configure aqui a conta bancária vinculada ao gateway de cartão de crédito. Estes dados são para uso interno do sistema de recebimento.
                       </div>
                       <div className="space-y-2">
                          <label className="text-xs font-bold text-slate-400 uppercase">Nome Titular (Recebedor)</label>
                          <input type="text" value={config.ccBeneficiary} onChange={e => setConfig({...config, ccBeneficiary: e.target.value})} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 outline-none" placeholder="Nome Completo" />
                       </div>
                       <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                             <label className="text-xs font-bold text-slate-400 uppercase">Banco</label>
                             <input type="text" value={config.ccBank} onChange={e => setConfig({...config, ccBank: e.target.value})} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 outline-none" placeholder="Nome ou Código" />
                          </div>
                          <div className="space-y-2">
                             <label className="text-xs font-bold text-slate-400 uppercase">CPF/CNPJ</label>
                             <input type="text" value={config.ccCpf} onChange={e => setConfig({...config, ccCpf: e.target.value})} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 outline-none" placeholder="Documento" />
                          </div>
                          <div className="space-y-2">
                             <label className="text-xs font-bold text-slate-400 uppercase">Agência</label>
                             <input type="text" value={config.ccAgency} onChange={e => setConfig({...config, ccAgency: e.target.value})} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 outline-none" placeholder="0000" />
                          </div>
                          <div className="space-y-2">
                             <label className="text-xs font-bold text-slate-400 uppercase">Conta</label>
                             <input type="text" value={config.ccAccount} onChange={e => setConfig({...config, ccAccount: e.target.value})} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 outline-none" placeholder="00000-0" />
                          </div>
                       </div>
                    </div>
                 ) : (
                    <div className="space-y-4 animate-in slide-in-from-right-4">
                       <div className="rounded-2xl border border-amber-500/20 bg-amber-500/10 p-4">
                          <div className="flex items-start gap-3">
                             <ShieldCheck className="mt-0.5 h-5 w-5 text-amber-300" />
                             <div className="space-y-1">
                                <h4 className="text-sm font-bold text-white">Verificações médicas pendentes</h4>
                                <p className="text-xs leading-relaxed text-amber-100/80">
                                   Aprove ou rejeite manualmente os CRM/RQE enviados pela equipe médica do OrthoPrecision.
                                </p>
                             </div>
                          </div>
                       </div>

                       {verificationNotice && (
                          <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/10 px-4 py-3 text-xs text-cyan-100">
                             {verificationNotice}
                          </div>
                       )}

                       {pendingVerificationUsers.length === 0 ? (
                          <div className="rounded-2xl border border-slate-700 bg-slate-900/60 px-5 py-8 text-center text-sm text-slate-400">
                             Nenhum médico com verificação pendente no momento.
                          </div>
                       ) : (
                          <div className="space-y-3">
                             {pendingVerificationUsers.map((item) => {
                               const submittedAtLabel =
                                 typeof item.submittedAt?.toDate === "function"
                                   ? item.submittedAt.toDate().toLocaleString(
                                       language === 'en' ? 'en-US' : language === 'es' ? 'es-ES' : 'pt-BR'
                                     )
                                   : t('reviewJustNow');
                               const isReviewing = verificationActionUid === item.uid;

                               return (
                                 <div
                                   key={item.uid}
                                   className="rounded-2xl border border-slate-700 bg-slate-900/70 p-4 shadow-[0_0_25px_rgba(15,23,42,0.25)]"
                                 >
                                   <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                                      <div className="space-y-3">
                                         <div>
                                            <p className="text-sm font-semibold text-white">{item.name || "Médico sem nome"}</p>
                                            <p className="text-xs text-slate-400">{item.email || "Email não informado"}</p>
                                         </div>

                                         <div className="flex flex-wrap gap-2">
                                            <span className="rounded-full border border-slate-700 bg-slate-800/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-200">
                                               CRM {item.crm || "--"}
                                            </span>
                                            <span className="rounded-full border border-slate-700 bg-slate-800/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-200">
                                               UF {item.crmState || "--"}
                                            </span>
                                            <span className="rounded-full border border-slate-700 bg-slate-800/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-200">
                                               RQE {item.rqe || "--"}
                                            </span>
                                         </div>

                                         <p className="text-xs text-slate-500">
                                            {t('reviewSubmittedAt')} {submittedAtLabel}
                                         </p>
                                      </div>

                                      <div className="flex gap-2 lg:min-w-55 lg:justify-end">
                                         <button
                                           onClick={() => handleReviewProfessionalVerification(item, "rejected")}
                                           disabled={isReviewing}
                                           className="flex-1 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-red-200 transition-colors hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                                         >
                                           {isReviewing ? t('reviewProcessing') : t('reviewReject')}
                                         </button>
                                         <button
                                           onClick={() => handleReviewProfessionalVerification(item, "verified")}
                                           disabled={isReviewing}
                                           className="flex-1 rounded-xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-emerald-100 transition-colors hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                                         >
                                           {isReviewing ? t('reviewProcessing') : t('reviewApprove')}
                                         </button>
                                      </div>
                                   </div>
                                 </div>
                               );
                             })}
                          </div>
                       )}
                    </div>
                 )}
              </div>

              {configTab !== 'verification' && (
                 <div className="p-4 border-t border-slate-700 bg-slate-900/50">
                    <button onClick={handleSaveConfig} className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold shadow-lg transition-all flex items-center justify-center gap-2">
                       <Save className="w-5 h-5" /> {t("saveSettings")}
                    </button>
                 </div>
              )}
           </div>
        </div>
      )}

      <div className="flex min-h-screen w-full">
      {/* Sidebar - Desktop */}
      {/* Ajuste: sidebar mais estreita (w-85 → w-64) */}
      <nav className="hidden md:flex flex-col w-52 min-[1200px]:w-60 min-[1440px]:w-64 bg-[linear-gradient(180deg,#020617_0%,#020617_40%,#020617_100%)] border-r border-[rgba(34,211,238,0.08)] shadow-[inset_-1px_0_0_rgba(255,255,255,0.03)] z-50 relative pointer-events-auto">
        {/* Ajuste: padding menor na sidebar (p-6 → p-4) */}
        <div className="p-4 flex flex-col items-center text-center">
          {/* Ajuste: logo menor (w-48 h-48 → w-36 h-36, mb-6 → mb-4) */}
          <div className="w-40 h-40 shrink-0 relative flex items-center justify-center mb-4 group cursor-pointer" onClick={() => setActiveTab(AppTab.PATIENTS)}>
              {/* Vertical Gradient Glow matching Tree Anatomy: Purple (Brain) -> Cyan (Circuits) -> Emerald (Leaves) */}
              <div className="absolute inset-0 bg-linear-to-t from-cyan-900/10 via-cyan-900/10 to-slate-900/10 blur-3xl opacity-60 group-hover:opacity-100 transition-opacity duration-700" />
              <img  
                src="/ortho-logo.png" 
                alt="OrthoPrecision" 
                className="w-full h-full object-contain relative z-10 drop-shadow-[0_0_12px_rgba(34,211,238,0.25)] transition-transform duration-500 group-hover:scale-105"
              />
          </div>
          <h1 className="text-[1.15rem] font-black tracking-tight text-transparent bg-clip-text bg-linear-to-r from-cyan-300 to-sky-300">
            {t('appTitle')}
          </h1>
          <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-[rgba(226,232,240,0.55)]">{t('appSubtitle')}</p>
        </div>
        
        {/* Ortho ID Action Area - Above Modules */}
        {userData && (
          <div className="px-2 mb-3"> {/* Ajuste: padding lateral e margem inferior menores */}
                <button 
                    onClick={() => setShowOrthoID(true)}
                    className="w-full rounded-[14px] border border-[rgba(34,211,238,0.15)] bg-[rgba(15,23,42,0.6)] p-3 flex items-center gap-3 group transition-all duration-150 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] hover:-translate-y-px hover:border-[rgba(34,211,238,0.35)] hover:shadow-[0_0_0_1px_rgba(34,211,238,0.08),0_12px_30px_rgba(0,0,0,0.22)]"
                >
                    <div className="w-10 h-10 rounded-full bg-[rgba(2,6,23,0.72)] border border-[rgba(34,211,238,0.18)] flex items-center justify-center text-sm font-bold text-[rgba(226,232,240,0.7)] group-hover:text-white group-hover:border-cyan-400 transition-colors overflow-hidden relative shadow-[0_0_18px_rgba(34,211,238,0.08)]">
                        {userData.photo ? (
                            <img 
                                src={userData.photo} 
                                alt="Profile" 
                                className="w-full h-full object-cover" 
                                style={{
                                    // Use % for consistent positioning in small avatar
                                    transform: `translate(${userData.photoConfig?.x || 0}%, ${userData.photoConfig?.y || 0}%) scale(${userData.photoConfig?.scale || 1})`
                                }}
                            />
                        ) : (
                            userData.name.substring(0,2).toUpperCase()
                        )}
                    </div>
                    <div className="text-left overflow-hidden">
                        <div className="flex items-center gap-2">
                            <p className="text-xs font-bold text-[rgba(226,232,240,0.9)] group-hover:text-cyan-300 transition-colors truncate w-32">Ortho ID</p>
                            {showVerifiedBadge && (
                                <img
                                  src="/images/verified-badge.svg"
                                  alt="Médico com CRM verificado"
                                  title="Médico com CRM verificado"
                                  className="h-4 w-4 shrink-0"
                                />
                            )}
                        </div>
                        <p className="text-[10px] text-[rgba(226,232,240,0.55)] truncate w-32">{t("viewCredential")}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[rgba(226,232,240,0.35)] group-hover:text-white ml-auto transition-colors" />
                </button>
            </div>
        )}

        {/* Ajuste: padding lateral menor na lista de módulos (px-4 → px-2), espaçamento vertical menor (space-y-2 → space-y-1) */}
        <div className="flex-1 px-2 space-y-1 overflow-y-auto custom-scrollbar">
          {tabs.map((tab) => {
            return (
              // Ajuste: botões dos módulos mais compactos (px-4 py-3 → px-3 py-2.5, gap-3 → gap-2)
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl transition-all duration-150 group border ${
                  activeTab === tab.id
                    ? 'border-[rgba(34,211,238,0.25)] bg-[linear-gradient(90deg,rgba(34,211,238,0.18),rgba(14,165,233,0.08))] shadow-[0_0_0_1px_rgba(34,211,238,0.08),0_10px_24px_rgba(0,0,0,0.18)]'
                    : 'border-transparent text-[rgba(226,232,240,0.7)] hover:bg-[rgba(34,211,238,0.08)] hover:border-[rgba(34,211,238,0.14)] hover:text-white'
                }`}
              >
                <tab.icon
                  className={`w-5 h-5 transition-all duration-300 ${
                    activeTab === tab.id
                      ? 'text-cyan-300 drop-shadow-[0_0_6px_rgba(34,211,238,0.18)]'
                      : 'text-[rgba(226,232,240,0.42)] group-hover:text-[rgba(226,232,240,0.85)]'
                  }`}
                />
                <span
                  className={`font-medium flex items-center gap-2 ${
                    activeTab === tab.id ? 'text-white' : 'text-[rgba(226,232,240,0.78)]'
                  }`}
                >
                  {tab.label}
                </span>
                {activeTab === tab.id && (
                  <ChevronRight className="w-4 h-4 ml-auto text-cyan-300 opacity-90" />
                )}
              </button>
            );
          })}
        </div>
        {/* Ajuste: padding menor no rodapé da sidebar (p-4 → p-3), espaçamento menor (space-y-4 → space-y-2) */}
        <div className="mt-2 rounded-[18px] border border-[rgba(34,211,238,0.10)] bg-[linear-gradient(180deg,rgba(15,23,42,0.55),rgba(2,6,23,0.85))] p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_18px_45px_rgba(0,0,0,0.24)]">
          <div className="space-y-3">
            {isTrialPlan ? (
              <div className="rounded-[12px] border border-[rgba(96,165,250,0.28)] bg-[rgba(15,23,42,0.6)] p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <div className="h-2 w-2 rounded-full bg-blue-400 shadow-[0_0_8px_rgba(96,165,250,0.45)]"></div>
                  <p className="text-xs font-bold tracking-[0.18em] text-blue-300">TRIAL</p>
                </div>
                {trialDaysRemaining !== null && (
                  <p className="text-center text-[10px] text-blue-400 font-medium">
                    {trialDaysRemaining <= 0
                      ? "Trial expirado"
                      : `${trialDaysRemaining} dia${trialDaysRemaining !== 1 ? "s" : ""} restante${trialDaysRemaining !== 1 ? "s" : ""}`}
                  </p>
                )}
                <button
                  onClick={() => onOpenSubscribeClick?.()}
                  className="mt-2 h-9 w-full rounded-[10px] border border-[rgba(34,211,238,0.16)] bg-gradient-to-r from-cyan-400 to-blue-500 px-3 text-[10px] font-bold uppercase tracking-[0.14em] text-white transition-all duration-150 hover:-translate-y-px hover:shadow-[0_14px_36px_rgba(34,211,238,0.32)]"
                >
                  Assinar plano
                </button>
              </div>
            ) : !isProPlan && !isPremiumPlan ? (
              <div className="rounded-[12px] border border-[rgba(34,211,238,0.18)] bg-[rgba(15,23,42,0.6)] p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                <button
                  onClick={() => {
                    onOpenSubscribeClick?.();
                  }}
                  className="h-10 w-full rounded-[12px] border border-[rgba(34,211,238,0.16)] bg-gradient-to-r from-cyan-400 to-blue-500 px-3 text-xs font-bold uppercase tracking-[0.14em] text-white transition-all duration-150 hover:-translate-y-px hover:shadow-[0_14px_36px_rgba(34,211,238,0.32)]"
                >
                  {t("upgrade")}
                </button>

                <p className="mt-2 text-center text-[10px] font-semibold uppercase tracking-[0.14em] text-[rgba(226,232,240,0.52)]">
                  {t("unlockModules")}
                </p>
              </div>
            ) : (
              <div className="rounded-[12px] border border-[rgba(255,255,255,0.06)] bg-[rgba(15,23,42,0.6)] px-3 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                <div className="flex items-center justify-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.45)]"></div>

                  <p className="text-xs font-bold tracking-[0.18em] text-emerald-300">
                    {planLabel}
                  </p>
                </div>
              </div>
            )}
            
            {/* Language Selector */}
            <div className="flex h-8 rounded-[12px] border border-[rgba(148,163,184,0.12)] bg-[rgba(2,6,23,0.65)] p-[3px]">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  className={`flex-1 rounded-[10px] text-xs font-bold transition-all duration-150 ${
                    language === lang.code 
                      ? 'border border-[rgba(34,211,238,0.24)] bg-[linear-gradient(135deg,rgba(34,211,238,0.22),rgba(37,99,235,0.16))] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]' 
                      : 'text-[rgba(226,232,240,0.55)] hover:text-white'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>

            {/* Ajuste: gap menor entre avatar e botões (gap-2 → gap-1) */}
            <div className="flex items-center gap-1 w-full min-w-0">
  <div className="flex items-center gap-3 h-[52px] px-3 py-2 rounded-[14px] bg-[rgba(15,23,42,0.72)] flex-1 min-w-0 max-w-full overflow-hidden border border-[rgba(148,163,184,0.12)]">
                  <div className="w-8 h-8 rounded-full bg-linear-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-xs font-bold overflow-hidden relative border border-white/10 shadow-[0_0_12px_rgba(34,211,238,0.18)]">
                    {userData?.photo ? (
                        <img 
                          src={userData.photo} 
                          alt="User" 
                          className="w-full h-full object-cover"
                          style={{
                              transform: `translate(${userData.photoConfig?.x || 0}%, ${userData.photoConfig?.y || 0}%) scale(${userData.photoConfig?.scale || 1})`
                          }}
                        />
                    ) : (
                        ((currentUser.email ?? currentUser.uid) || "")
    .substring(0, 2)
    .toUpperCase()
                    )}
                  </div>
                  <div className="flex-1 min-w-0 max-w-full overflow-hidden">
  <p
    title={currentUser.displayName ?? currentUser.email ?? currentUser.uid}
    className="block w-full truncate text-[13px] font-bold text-[rgba(226,232,240,0.92)]"
  >
    {currentUser.displayName ?? currentUser.email ?? currentUser.uid}
  </p>

  <p className="block w-full truncate text-[10px] uppercase tracking-wide text-[rgba(148,163,184,0.8)]">
    {isAdmin ? t('manager') : t('surgeon')}
  </p>
</div>
                  {/* Backup Button */}
                  <button 
                    onClick={handleGoogleDriveBackup}
                    disabled={isBackingUp}
                    className={`p-1.5 rounded transition-colors ${
                      backupStatus === 'success' ? 'text-emerald-400 hover:bg-emerald-500/20' :
                      backupStatus === 'error' ? 'text-red-400 hover:bg-red-500/20' :
                      isBackingUp ? 'text-blue-400 animate-pulse' :
                      'text-[rgba(226,232,240,0.45)] hover:bg-[rgba(34,211,238,0.08)] hover:text-cyan-300'
                    }`}
                    title={t("googleDriveBackup")}
                  >
                    <Cloud className="w-4 h-4" />
                  </button>

                  {/* Logout Button */}
                  <button 
                    onClick={onLogout}
                    className="p-1.5 hover:bg-red-500/20 hover:text-red-400 text-[rgba(226,232,240,0.45)] rounded transition-colors"
                    title={t("logout")}
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
                
                {isAdmin && (
                  <button 
                    onClick={() => setShowAdminConfig(true)}
                    className="p-3 bg-[rgba(15,23,42,0.65)] hover:bg-[rgba(34,211,238,0.08)] border border-[rgba(148,163,184,0.12)] rounded-[12px] text-[rgba(226,232,240,0.55)] hover:text-cyan-300 transition-all"
                    title={t("adminSettings")}
                  >
                    <Settings className="w-5 h-5" />
                  </button>
                )}
            </div>
          </div>
          </div>
        </nav>

        {/* Main Content Area */}
        {/* Ajuste: área principal com padding menor em desktop (md:p-5 → md:p-3) */}
        <main className="flex min-w-0 flex-1 flex-col relative overflow-hidden bg-slate-900">
          
          {/* Header - Mobile */}
          <header className="md:hidden bg-slate-950/90 backdrop-blur-md border-b border-slate-800 p-4 flex items-center justify-between sticky top-0 z-50 shadow-lg">
            <h1 className="text-xl font-bold flex items-center gap-3 overflow-hidden">
              <div className="w-10 h-10 shrink-0 relative flex items-center justify-center">
                  <img src="/ortho-logo.png" alt="Logo" className="w-full h-full object-contain drop-shadow-[0_0_8px_rgba(34,211,238,0.5)]" />
              </div>
              <span className="text-transparent bg-clip-text bg-linear-to-r from-cyan-400 to-purple-400 tracking-tight">{t('appTitle')}</span>
            </h1>
            <div className="flex gap-2">
              <button onClick={onLogout} className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center border border-slate-700 text-red-400">
                <LogOut className="w-4 h-4" />
              </button>
              <button onClick={() => setLanguage(language === 'pt' ? 'en' : language === 'en' ? 'es' : 'pt')} className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center border border-slate-700 text-xs font-bold uppercase text-slate-300">
                  {language}
              </button>
              <button onClick={() => setShowOrthoID(true)} className="w-9 h-9 rounded-full bg-linear-to-br from-cyan-600 to-purple-600 flex items-center justify-center text-white shadow-lg border border-white/10">
                <IdCard className="w-5 h-5" />
              </button>
            </div>
          </header>

          {/* ACTIVE PATIENT BANNER */}
          {activePatient && (
            <div className="bg-linear-to-r from-slate-900 to-slate-800 border-b border-slate-700 px-6 py-3 flex justify-between items-center z-10 animate-in slide-in-from-top-2 shadow-md">
              <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-linear-to-br from-cyan-600 to-blue-700 flex items-center justify-center text-white font-bold text-sm shadow-lg border border-white/10">
                    {activePatient.name.substring(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white flex items-center gap-2">
                        {activePatient.name}
                        <span className="px-2 py-0.5 rounded text-[10px] bg-slate-950 border border-slate-600 text-slate-400 uppercase tracking-wider font-semibold">
                          {activePatient.status}
                        </span>
                    </h3>
                    <div className="flex gap-3 text-xs text-slate-400 font-medium">
                        <span>{activePatient.age} anos</span>
                        <span className="text-slate-600">•</span>
                        <span>{translatePatientSummaryAnatomy(activePatient.anatomy)} ({getPatientSummarySideLabel(activePatient.side)})</span>
                    </div>
                  </div>
              </div>
              
              {activeTab !== AppTab.PATIENTS && (
                <button 
                  onClick={() => setActiveTab(AppTab.PATIENTS)}
                  className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg text-xs font-bold uppercase tracking-wide transition-colors border border-slate-600 hover:border-cyan-500/50"
                >
                  Voltar ao Painel
                </button>
              )}
            </div>
        )}

          <div className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden p-2 md:p-3 bg-[var(--op-bg-deep)]"> {/* Ajuste: padding menor */}
          {children}
        </div>

        {/* Mobile Bottom Nav */}
        <nav className="md:hidden bg-slate-950/90 backdrop-blur-lg border-t border-slate-800 px-2 py-3 flex justify-around items-center z-50 safe-area-pb">
          {tabs.map((tab) => {
  return (
    <button
      key={tab.id}
      onClick={() => setActiveTab(tab.id)}
      className={`flex flex-col items-center gap-1 transition-all ${
        activeTab === tab.id ? 'text-cyan-400 scale-110' : 'text-slate-500'
      }`}
    >
      <tab.icon
        className={`w-6 h-6 ${
          activeTab === tab.id
            ? 'drop-shadow-[0_0_8px_rgba(34,211,238,0.5)]'
            : ''
        }`}
      />

      <span className="text-[9px] font-bold uppercase tracking-wide flex items-center gap-1">
        {tab.label}
      </span>
    </button>
  );
})}
        </nav>
      </main>
      </div>
    </div>
  );
};

export default Layout;
